// Modern Bundle Builder JavaScript with Recharge Integration
class BundleBuilder {
  constructor() {
    this.bundleItems = new Map();
    this.productQuantities = new Map(); // Track quantity of each product
    this.currentPackSize = 4;
    this.currentPackPrice = 24;
    this.maxItems = 4;
    this.isSubscription = false; // Track subscription vs one-time purchase
    this.shippingProtection = false; // Track shipping protection opt-in
    this.wasAutoUpsized = false; // Track if auto-upsize happened (enables auto-downsize)
    this.settings = window.customBundleSettings || {};
    this.debug = new URLSearchParams(window.location.search).has('debug');

    this.init();
  }

  // Get available pack sizes sorted ascending
  getAvailablePackSizes() {
    const packSizes = this.settings.packSizes || [];
    return [...packSizes].sort((a, b) => a.size - b.size);
  }

  // Format a dollar price: show cents only when not a whole dollar (e.g. 64.80 → "64.80", 108 → "108")
  formatSubPrice(amount) {
    const rounded = Math.round(amount * 100) / 100;
    return rounded % 1 === 0 ? `${rounded}` : `${rounded.toFixed(2)}`;
  }

  // Get subscription discount percentage for the current pack size
  getDiscountForPackSize(packSize) {
    const size = packSize || this.currentPackSize;
    const discounts = this.settings.subscriptionDiscounts || {};
    // Look up by pack size key, fall back to first available, then 5%
    if (discounts[String(size)] !== undefined) return discounts[String(size)];
    const packSizes = this.settings.packSizes || [];
    const pack = packSizes.find(p => p.size === size);
    if (pack && pack.discount !== undefined) return pack.discount;
    return 5;
  }

  // Get milestones array for progress tracking [0, size1, size2, ...]
  getMilestones() {
    const sizes = this.getAvailablePackSizes().map(p => p.size);
    return [0, ...sizes];
  }

  // Subscription price for the current pack. The discount applies to the
  // compare price (list) when one is configured for the pack, otherwise to
  // the variant price — matching what Recharge actually bills.
  getSubscriptionPrice() {
    const basePrice = this.currentPackPrice || 0;
    const discountPct = this.getDiscountForPackSize() / 100;
    const pack = (this.settings.packSizes || []).find(p => p.size === this.currentPackSize);
    const anchor = pack && pack.comparePrice > basePrice ? pack.comparePrice : basePrice;
    return anchor * (1 - discountPct);
  }

  init() {
    this.bindEvents();
    this.boxSizeSelected = false;
    this.fillModeSelected = false;

    // Set default box size (smallest available pack)
    const availablePacks = this.getAvailablePackSizes();
    const defaultPack = availablePacks[0];
    const defaultBoxSizeBtn = defaultPack
      ? document.querySelector(`.box-size-btn[data-box-size="${defaultPack.size}"]`)
      : document.querySelector('.box-size-btn');

    if (defaultBoxSizeBtn) {
      defaultBoxSizeBtn.classList.add('selected');
      const size = parseInt(defaultBoxSizeBtn.dataset.boxSize);
      this.currentPackSize = size;
      this.currentPackPrice = parseFloat(defaultBoxSizeBtn.dataset.boxPrice) || (defaultPack?.price || 40);
      this.maxItems = size;
      this.boxSizeSelected = true;
    }

    // Initialize tab functionality
    this.initializeTabs();

    // Update slot visibility
    this.updateSlotVisibility();
    this.updateDisplay();

    // Initialize image carousel
    this.initImageCarousel();

    // Initialize with one-time purchase as default
    this.toggleSubscription(false);

    // Initialize shipping protection price for default pack size
    this.updateShippingProtectionPrice();
  }

  initImageCarousel() {
    // Auto-rotate product images on hover
    const productCards = document.querySelectorAll('.bundle-product-card');

    productCards.forEach(card => {
      const imageWrapper = card.querySelector('.bundle-product-card__image-wrapper');
      const images = card.querySelectorAll('.bundle-product-card__image');

      if (!imageWrapper || images.length <= 1) return;

      let currentIndex = 0;
      let intervalId = null;

      // Show first image initially
      images.forEach((img, idx) => {
        img.style.opacity = idx === 0 ? '1' : '0';
        img.style.zIndex = idx === 0 ? '2' : '1';
      });

      // Start carousel on hover
      imageWrapper.addEventListener('mouseenter', () => {
        if (intervalId) return; // Already running

        // Immediately show 2nd image if it exists
        if (images.length > 1) {
          images[0].style.opacity = '0';
          images[0].style.zIndex = '1';
          images[1].style.opacity = '1';
          images[1].style.zIndex = '2';
          currentIndex = 1;
        }

        // If there are 3+ images, start auto-rotation from 3rd image
        if (images.length > 2) {
          intervalId = setInterval(() => {
            // Hide current image
            images[currentIndex].style.opacity = '0';
            images[currentIndex].style.zIndex = '1';

            // Move to next image
            currentIndex = currentIndex + 1;
            if (currentIndex >= images.length) {
              currentIndex = 2; // Loop back to 3rd image
            }

            // Show next image
            images[currentIndex].style.opacity = '1';
            images[currentIndex].style.zIndex = '2';
          }, 2000); // 2 second interval
        }
      });

      // Stop carousel on mouse leave and reset
      imageWrapper.addEventListener('mouseleave', () => {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }

        // Reset to first image
        images.forEach((img, idx) => {
          img.style.opacity = idx === 0 ? '1' : '0';
          img.style.zIndex = idx === 0 ? '2' : '1';
        });
        currentIndex = 0;
      });
    });
  }

  scrollToElement(selector) {
    const element = document.querySelector(selector);
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }

  bindEvents() {
    // Box size selection (top nav buttons)
    document.addEventListener('click', (e) => {
      if (e.target.closest('.box-size-btn[data-box-size]')) {
        e.preventDefault();
        this.selectBoxSize(e.target.closest('.box-size-btn[data-box-size]'));
      }
    });

    // Pack size selection (legacy sidebar)
    document.addEventListener('click', (e) => {
      if (e.target.closest('.bundle-option[data-pack-size]')) {
        e.preventDefault();
        this.selectPackSize(e.target.closest('.bundle-option[data-pack-size]'));
      }
    });

    // Manual select button
    document.addEventListener('click', (e) => {
      if (e.target.closest('.manual-select-btn')) {
        e.preventDefault();
        this.selectManualMode();
      }
    });

    // Category filter buttons (both anchor and button elements)
    document.addEventListener('click', (e) => {
      if (e.target.closest('.category-filter-btn')) {
        e.preventDefault();
        this.toggleCategoryFilter(e.target.closest('.category-filter-btn'));
      }
    });

    // Add to bundle buttons
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('bundle-add-button') || e.target.classList.contains('bundle-add-button-yellow')) {
        e.preventDefault();
        this.addToBundle(e.target);
      }
    });

    // Notify Me buttons (out of stock - STOQ/RestockRocket)
    document.addEventListener('click', (e) => {
      if (e.target.closest('.bundle-notify-btn')) {
        e.preventDefault();
        const btn = e.target.closest('.bundle-notify-btn');
        const productCard = btn.closest('.bundle-product-card');
        const productData = JSON.parse(productCard.querySelector('.product-variants-data').textContent);
        const variantId = btn.dataset.variantId;

        if (window._RestockRocket) {
          const stoqProduct = {
            id: productData.product_id,
            variants: productData.variants.map(v => ({ id: v.id, available: v.available }))
          };
          window._RestockRocket.openModal(stoqProduct, String(variantId));
        } else {
          console.warn('RestockRocket (STOQ) not loaded');
        }
      }
    });

    // Remove from bundle buttons
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('bundle-item-remove')) {
        e.preventDefault();
        this.removeFromBundle(e.target.closest('.bundle-item-slot'));
      }
    });

    // Mobile sticky bar - Review Box button (opens modal)
    document.addEventListener('click', (e) => {
      if (e.target.closest('#mobile-review-btn')) {
        e.preventDefault();
        this.openMobileReviewModal();
      }
    });

    // Mobile review modal - Close button
    document.addEventListener('click', (e) => {
      if (e.target.closest('#mobile-review-close')) {
        e.preventDefault();
        this.closeMobileReviewModal();
      }
    });

    // Mobile review modal - Quantity buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('.mobile-review-qty-btn')) {
        e.preventDefault();
        const btn = e.target.closest('.mobile-review-qty-btn');
        const productId = btn.dataset.productId;
        const action = btn.dataset.action;

        if (action === 'increase') {
          this.increaseQuantityById(parseInt(productId));
        } else if (action === 'decrease') {
          this.decreaseQuantityById(parseInt(productId));
        }

        // Update the modal
        this.updateMobileReviewModal();
      }
    });

    // Mobile review modal - Checkout button (respects current purchase type)
    document.addEventListener('click', (e) => {
      if (e.target.closest('#mobile-checkout-btn')) {
        e.preventDefault();
        this.closeMobileReviewModal();
        this.addBundleToCart();
      }
    });


    // Add to cart
    document.addEventListener('click', (e) => {
      if (e.target.closest('.fsb__bundle-add-to-cart')) {
        e.preventDefault();
        this.addBundleToCart();
      }
    });

    // Widget checkout button
    document.addEventListener('click', (e) => {
      if (e.target.closest('.bundle-widget-checkout-btn')) {
        e.preventDefault();
        this.addBundleToCart();
      }
    });

    // Secondary one-time button (stays in one-time mode and adds to cart)
    document.addEventListener('click', (e) => {
      if (e.target.closest('.bundle-widget-onetime-btn')) {
        e.preventDefault();
        // Keep one-time mode and add to cart (without subscription)
        this.toggleSubscription(false);
        setTimeout(() => {
          this.addBundleToCart();
        }, 100);
      }
    });

    // Mystery flavor widget toggle button
    document.addEventListener('click', (e) => {
      const mysteryBtn = e.target.closest('.mystery-flavor-toggle-btn');
      if (mysteryBtn) {
        e.preventDefault();
        const wasActive = mysteryBtn.classList.contains('active');
        mysteryBtn.classList.toggle('active');

        // Toggle the mystery checkbox (if it exists in the main UI)
        const mysteryCheckbox = document.querySelector('.mystery-checkbox');
        if (mysteryCheckbox) {
          mysteryCheckbox.checked = mysteryBtn.classList.contains('active');
          mysteryCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Add random product if toggling ON
        if (!wasActive && mysteryBtn.classList.contains('active')) {
          this.addMysteryFlavor();
        } else if (wasActive && !mysteryBtn.classList.contains('active')) {
          // Remove mystery flavor if toggling OFF
          this.removeMysteryFlavor();
        }
      }
    });

    // Mystery flavor toggle
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('mystery-checkbox')) {
        this.toggleMysteryFlavor(e.target.checked);
      }
    });

    // Shipping protection checkbox (sync desktop and mobile)
    document.addEventListener('change', (e) => {
      if (e.target.id === 'shipping-protection-checkbox' || e.target.id === 'mobile-shipping-protection-checkbox') {
        this.shippingProtection = e.target.checked;
        // Sync both checkboxes
        const desktopCheckbox = document.getElementById('shipping-protection-checkbox');
        const mobileCheckbox = document.getElementById('mobile-shipping-protection-checkbox');
        if (desktopCheckbox) desktopCheckbox.checked = e.target.checked;
        if (mobileCheckbox) mobileCheckbox.checked = e.target.checked;
      }
    });

    // Subscription toggle - direct binding for reliability
    const subscriptionCheckbox = document.querySelector('.subscription-checkbox');
    if (subscriptionCheckbox) {
      subscriptionCheckbox.addEventListener('change', (e) => {
        this.toggleSubscription(e.target.checked);
      });
    }

    // Purchase Type Toggle (New UI)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.purchase-option-btn');
      if (btn) {
        e.preventDefault();
        const type = btn.dataset.type;
        const isSub = type === 'subscribe';

        // Update checkbox state (this triggers change event if we didn't intercept clicks?)
        // Actually, we should call toggleSubscription directly to be safe
        this.toggleSubscription(isSub);

        const subCheckbox = document.querySelector('.subscription-checkbox');
        if (subCheckbox) subCheckbox.checked = isSub;

        // Scroll to flavor filter section
        this.scrollToElement('.fsb__filter-section');
      }
    });

    // Tab navigation
    document.addEventListener('click', (e) => {
      if (e.target.closest('.tabs__navigation-link')) {
        e.preventDefault();
        this.switchTab(e.target.closest('.tabs__navigation-link'));
      }
    });

    // Quantity controls (product card)
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('quantity-increase')) {
        e.preventDefault();
        this.increaseQuantity(e.target);
      } else if (e.target.classList.contains('quantity-decrease')) {
        e.preventDefault();
        this.decreaseQuantity(e.target);
      }
    });

    // Bundle sidebar quantity controls
    document.addEventListener('click', (e) => {
      const increaseBtn = e.target.closest('.bundle-qty-increase');
      const decreaseBtn = e.target.closest('.bundle-qty-decrease');

      if (increaseBtn) {
        e.preventDefault();
        this.increaseQuantity(increaseBtn);
      } else if (decreaseBtn) {
        e.preventDefault();
        this.decreaseQuantity(decreaseBtn);
      }
    });

    // Nutrition label modal
    document.addEventListener('click', (e) => {
      const nutritionLink = e.target.closest('.nutrition-label-link');
      if (nutritionLink) {
        e.preventDefault();
        this.showNutritionModal(nutritionLink);
      } else if (e.target.classList.contains('nutrition-modal-close') || e.target.classList.contains('nutrition-modal-overlay')) {
        e.preventDefault();
        this.hideNutritionModal();
      } else if (e.target.id === 'nutrition-add-to-box') {
        e.preventDefault();
        this.addFromNutritionModal();
      }
    });
  }

  initializeTabs() {
    const firstTab = document.querySelector('.tabs__navigation-link');
    if (firstTab) {
      firstTab.classList.add('active');
    }
  }

  selectBoxSize(boxElement) {
    // Remove active state from all box size buttons
    document.querySelectorAll('.box-size-btn[data-box-size]').forEach(btn => {
      btn.classList.remove('selected');
    });

    // Add active state to selected button
    boxElement.classList.add('selected');

    // Update pack settings
    this.currentPackSize = parseInt(boxElement.dataset.boxSize);
    this.currentPackPrice = parseFloat(boxElement.dataset.boxPrice);
    this.maxItems = this.currentPackSize;
    this.boxSizeSelected = true;
    this.wasAutoUpsized = false; // Reset auto-upsize flag on manual selection

    // If a category filter is active, refill with new pack size
    const activeFilter = document.querySelector('.category-filter-btn.active');
    if (activeFilter) {
      const category = activeFilter.dataset.filter;
      this.autoFillBundle(category);
    } else {
      // Clear excess items if switching to smaller pack
      if (this.bundleItems.size > this.maxItems) {
        const itemsToRemove = Array.from(this.bundleItems.keys()).slice(this.maxItems);
        itemsToRemove.forEach(key => {
          const item = this.bundleItems.get(key);
          if (item) {
            this.removeSelectedState(item.productId);
            this.hideQuantityControls(item.productId);
          }
          this.bundleItems.delete(key);
        });
      }
    }

    // Update UI
    this.updateDisplay();
    this.updateAddToCartButton();
    this.renderBundleItems();
    this.updateSlotVisibility();

    // Update shipping protection price display for new pack size
    this.updateShippingProtectionPrice();
  }

  selectManualMode() {
    this.fillModeSelected = true;

    // Mark manual button as selected
    const manualBtn = document.querySelector('.manual-select-btn');
    if (manualBtn) {
      manualBtn.classList.add('selected');
    }

    // Remove active state from category filters
    document.querySelectorAll('.category-filter-btn').forEach(btn => {
      btn.classList.remove('active');
    });

    // Show all products (remove any filters)
    document.querySelectorAll('.bundle-product-card').forEach(card => {
      card.classList.remove('filtered-out');
    });

    // Clear any existing bundle
    this.clearBundle();

    this.showSuccess('Select your favorite flavors manually!');
  }

  selectPackSize(packElement) {
    // Remove active state from all options
    document.querySelectorAll('.bundle-option[data-pack-size]').forEach(option => {
      option.classList.remove('selected');
    });

    // Add active state to selected option
    packElement.classList.add('selected');

    // Update pack settings
    this.currentPackSize = parseInt(packElement.dataset.packSize);
    this.currentPackPrice = parseFloat(packElement.dataset.packPrice);
    this.maxItems = this.currentPackSize;

    // Update UI
    this.updateDisplay();
    this.updateAddToCartButton();

    // Clear excess items if switching to smaller pack
    if (this.bundleItems.size > this.maxItems) {
      const itemsToRemove = Array.from(this.bundleItems.keys()).slice(this.maxItems);
      itemsToRemove.forEach(key => {
        this.bundleItems.delete(key);
      });
    }

    this.renderBundleItems();
    this.updateSlotVisibility();
  }

  toggleCategoryFilter(filterButton) {
    const filter = filterButton.dataset.filter;
    const filterColor = filterButton.dataset.color;

    // Toggle active state on the clicked filter button
    const isActive = filterButton.classList.contains('active');

    // Remove active state and reset styles for all category filter buttons
    document.querySelectorAll('.category-filter-btn').forEach(btn => {
      btn.classList.remove('active');
      // Reset to original border color from data attribute
      const originalColor = btn.dataset.color;
      btn.style.borderColor = originalColor || '#d1d5db';
      btn.style.backgroundColor = 'white';
      btn.style.color = '#374151';
    });

    // Remove selected state from manual button
    const manualBtn = document.querySelector('.manual-select-btn');
    if (manualBtn) {
      manualBtn.classList.remove('selected');
      manualBtn.classList.remove('active');
    }

    // Get all product cards
    const productCards = document.querySelectorAll('.bundle-product-card[data-categories]');

    if (isActive) {
      // If clicking on already active filter, show all products and clear bundle
      productCards.forEach(card => {
        card.classList.remove('filtered-out');
      });
      // Clear the bundle when deselecting a filter
      this.clearBundle();
      // Re-select manual button
      if (manualBtn) {
        manualBtn.classList.add('selected');
      }
    } else {
      this.fillModeSelected = true;

      // Activate the clicked filter and apply color as background
      filterButton.classList.add('active');
      if (filterColor) {
        filterButton.style.borderColor = filterColor;
        filterButton.style.backgroundColor = this.lightenColorForBg(filterColor);
        filterButton.style.color = '#374151';
      }

      // Keep all products visible — just auto-fill the box with matching flavors
      productCards.forEach(card => {
        card.classList.remove('filtered-out');
      });

      // Auto-fill the bundle with products from this category
      this.autoFillBundle(filter);

      // Scroll to product grid after filtering
      this.scrollToElement('.fsb__products-section');
    }
  }

  autoFillBundle(category) {
    // Clear existing bundle first
    this.clearBundle();

    // Check for predefined defaults for specific categories
    const defaults = this.getCategoryDefaults(category);

    if (defaults) {
      this.fillWithDefaults(defaults, category);
      return;
    }

    // Fallback: auto-fill with matching products
    const matchingCards = Array.from(document.querySelectorAll(`.bundle-product-card[data-categories*="${category}"]`));

    // Define all flavor categories (excluding 'favorites' which is a popularity tag)
    const flavorCategories = ['spice', 'creamy', 'minty', 'fruity'];
    const otherFlavorCategories = flavorCategories.filter(c => c !== category);

    // Separate products into unique (only this flavor) and shared (multiple flavors)
    const uniqueProducts = [];
    const sharedProducts = [];

    matchingCards.forEach(card => {
      const categories = (card.dataset.categories || '').trim().split(/\s+/).filter(c => c);
      const otherFlavorsCount = otherFlavorCategories.filter(c => categories.includes(c)).length;

      if (otherFlavorsCount === 0) {
        uniqueProducts.push(card);
      } else {
        sharedProducts.push({ card, otherFlavorsCount });
      }
    });

    sharedProducts.sort((a, b) => a.otherFlavorsCount - b.otherFlavorsCount);
    const sortedCards = [...uniqueProducts, ...sharedProducts.map(p => p.card)];

    let addedCount = 0;

    sortedCards.forEach(card => {
      if (addedCount >= this.maxItems) return;
      const addButton = card.querySelector('.bundle-add-button-yellow');
      if (addButton) {
        this.addToBundle(addButton);
        addedCount++;
      }
    });

    if (addedCount < this.maxItems && sortedCards.length > 0) {
      let duplicateIndex = 0;
      while (addedCount < this.maxItems) {
        const card = sortedCards[duplicateIndex % sortedCards.length];
        const increaseButton = card.querySelector('.quantity-increase');
        if (increaseButton) {
          this.increaseQuantity(increaseButton);
          addedCount++;
        }
        duplicateIndex++;
        if (duplicateIndex > this.maxItems * 2) break;
      }
    }

    if (addedCount > 0) {
      const categoryName = category.charAt(0).toUpperCase() + category.slice(1);
      this.showSuccess(`Added ${addedCount} ${categoryName} flavours to your ShipMint<sup>™</sup>!`);
    }
  }

  // Get predefined default selections for a category based on pack size
  // Uses product handles for matching (e.g., 'keylime-pie' matches 'keylime-pie-buttermints')
  getCategoryDefaults(category) {
    const packSize = this.maxItems;

    // Fan Favorites defaults by pack size (curated, not the "Best Selling" auto-fill)
    if (category === 'favorites') {
      if (packSize === 4) {
        // 4 pack: original, orange cream, key lime pie, cinnamon cheesecake
        return [
          { name: 'original', qty: 1 },
          { name: 'orange-cream', qty: 1 },
          { name: 'keylime-pie', qty: 1 },
          { name: 'cinnamon-cheesecake', qty: 1 }
        ];
      } else if (packSize === 6) {
        // 6 pack: 4 pack + coconut cream and coffee
        return [
          { name: 'original', qty: 1 },
          { name: 'orange-cream', qty: 1 },
          { name: 'keylime-pie', qty: 1 },
          { name: 'cinnamon-cheesecake', qty: 1 },
          { name: 'coconut-cream', qty: 1 },
          { name: 'coffee', qty: 1 }
        ];
      } else if (packSize === 10) {
        // 10 pack: 4 pack x 2 + 1 coconut cream + 1 coffee
        return [
          { name: 'original', qty: 2 },
          { name: 'orange-cream', qty: 2 },
          { name: 'keylime-pie', qty: 2 },
          { name: 'cinnamon-cheesecake', qty: 2 },
          { name: 'coconut-cream', qty: 1 },
          { name: 'coffee', qty: 1 }
        ];
      } else if (packSize === 15) {
        // 15 pack: 10 pack base + 2 spearmint + 2 spiced eggnog + 1 more original (3 original total)
        return [
          { name: 'original', qty: 3 },
          { name: 'orange-cream', qty: 2 },
          { name: 'keylime-pie', qty: 2 },
          { name: 'cinnamon-cheesecake', qty: 2 },
          { name: 'coconut-cream', qty: 1 },
          { name: 'coffee', qty: 1 },
          { name: 'spearmint', qty: 2 },
          { name: 'spiced-eggnog', qty: 2 }
        ];
      }
      // 20 pack not live — falls through to "Best Selling" auto-fill
    }

    // Creamy defaults by pack size
    if (category === 'creamy') {
      if (packSize === 4) {
        // 4 pack: keylime pie, orange cream, strawberry cream, coconut cream
        return [
          { name: 'keylime-pie', qty: 1 },
          { name: 'orange-cream', qty: 1 },
          { name: 'strawberry-cream', qty: 1 },
          { name: 'coconut-cream', qty: 1 }
        ];
      } else if (packSize === 6) {
        // 6 pack: 4 pack + coffee and blueberry cream
        return [
          { name: 'keylime-pie', qty: 1 },
          { name: 'orange-cream', qty: 1 },
          { name: 'strawberry-cream', qty: 1 },
          { name: 'coconut-cream', qty: 1 },
          { name: 'coffee', qty: 1 },
          { name: 'blueberry-cream', qty: 1 }
        ];
      } else if (packSize === 10) {
        // 10 pack: 4 pack x 2 + 2 coffee
        return [
          { name: 'keylime-pie', qty: 2 },
          { name: 'orange-cream', qty: 2 },
          { name: 'strawberry-cream', qty: 2 },
          { name: 'coconut-cream', qty: 2 },
          { name: 'coffee', qty: 2 }
        ];
      } else if (packSize === 15) {
        // 15 pack: 6 pack x 2 + 3 blueberry cream
        return [
          { name: 'keylime-pie', qty: 2 },
          { name: 'orange-cream', qty: 2 },
          { name: 'strawberry-cream', qty: 2 },
          { name: 'coconut-cream', qty: 2 },
          { name: 'coffee', qty: 2 },
          { name: 'blueberry-cream', qty: 3 },
          { name: 'banana-cream', qty: 2 }
        ];
      } else if (packSize === 20) {
        // 20 pack: spread across all creamy flavors
        return [
          { name: 'keylime-pie', qty: 3 },
          { name: 'orange-cream', qty: 3 },
          { name: 'strawberry-cream', qty: 3 },
          { name: 'coconut-cream', qty: 3 },
          { name: 'coffee', qty: 3 },
          { name: 'blueberry-cream', qty: 3 },
          { name: 'banana-cream', qty: 2 }
        ];
      }
    }

    // No predefined defaults for this category/size
    return null;
  }

  // Check if a product card is in stock
  isCardInStock(card) {
    const variantsEl = card.querySelector('.product-variants-data');
    if (!variantsEl) return false;
    try {
      const productData = JSON.parse(variantsEl.textContent);
      return productData.variants.some(v => v.available);
    } catch {
      return false;
    }
  }

  // Find a product card by name (handle or title partial match)
  findCardByName(cards, name) {
    const searchName = name.toLowerCase();
    return cards.find(c => {
      const handle = (c.dataset.productHandle || '').toLowerCase();
      const title = (c.querySelector('.bundle-product-card__title')?.textContent || '').toLowerCase();
      return handle.includes(searchName.replace(/\s+/g, '-')) || title.includes(searchName);
    });
  }

  // Get in-stock substitute cards not already used and not reserved by another default.
  // For real flavor categories (creamy/spice/minty/fruity) substitutes stay in-category;
  // for "favorites" (curated mix) any in-stock flavor is a valid fallback.
  getAvailableSubstitutes(cards, usedHandles, intendedHandles = new Set(), category = 'creamy') {
    return cards.filter(c => {
      const categories = (c.dataset.categories || '').toLowerCase();
      if (category !== 'favorites' && !categories.includes(category)) return false;
      const handle = (c.dataset.productHandle || '').toLowerCase();
      if (usedHandles.has(handle)) return false;
      // Don't steal a flavor another default is going to add itself
      if (intendedHandles.has(handle)) return false;
      return this.isCardInStock(c);
    });
  }

  // Fill bundle with predefined defaults
  fillWithDefaults(defaults, category = 'creamy') {
    let addedCount = 0;
    const cards = Array.from(document.querySelectorAll('.bundle-product-card'));
    const usedHandles = new Set();

    // Reserve the handles of every in-stock default up front so a substitute for an
    // out-of-stock flavor never picks a flavor another default is going to add itself
    // (which would silently collapse into one bag and inflate the count).
    const intendedHandles = new Set();
    defaults.forEach(item => {
      const c = this.findCardByName(cards, item.name);
      if (c && this.isCardInStock(c)) {
        intendedHandles.add((c.dataset.productHandle || '').toLowerCase());
      }
    });

    if (this.debug) {
      console.log('[fillWithDefaults] Looking for products:', defaults.map(d => d.name));
      console.log('[fillWithDefaults] Available products:', cards.map(c => c.dataset.productHandle || c.querySelector('.bundle-product-card__title')?.textContent));
    }

    defaults.forEach(item => {
      let card = this.findCardByName(cards, item.name);
      const cardHandle = card ? (card.dataset.productHandle || '').toLowerCase() : '';

      // If card not found or out of stock, find an in-stock substitute
      if (!card || !this.isCardInStock(card)) {
        if (this.debug) {
          console.log(`[fillWithDefaults] "${item.name}" is ${card ? 'OUT OF STOCK' : 'NOT FOUND'}, looking for substitute`);
        }
        if (card) usedHandles.add(cardHandle);
        const substitutes = this.getAvailableSubstitutes(cards, usedHandles, intendedHandles, category);
        card = substitutes.length > 0 ? substitutes[0] : null;
        if (card && this.debug) {
          console.log(`[fillWithDefaults] Substituting with "${card.dataset.productHandle}"`);
        }
      }

      if (card) {
        const handle = (card.dataset.productHandle || '').toLowerCase();
        usedHandles.add(handle);
      }

      if (this.debug) {
        console.log(`[fillWithDefaults] Looking for "${item.name}":`, card ? 'FOUND' : 'NOT FOUND');
      }

      if (card) {
        for (let i = 0; i < item.qty && addedCount < this.maxItems; i++) {
          if (i === 0) {
            // Add first item using add button
            const addButton = card.querySelector('.bundle-add-button-yellow');
            if (addButton && this.addToBundle(addButton)) {
              addedCount++;
            }
          } else {
            // Add additional quantity using increase button
            const increaseButton = card.querySelector('.quantity-increase');
            if (increaseButton && this.increaseQuantity(increaseButton)) {
              addedCount++;
            }
          }
        }
      }
    });

    if (addedCount > 0) {
      const label = category === 'favorites'
        ? 'Fan Favorite'
        : category.charAt(0).toUpperCase() + category.slice(1);
      this.showSuccess(`Added ${addedCount} ${label} Flavors to your ShipMint<sup>™</sup>!`);
    }
  }

  clearBundle() {
    // Clear all items from the bundle
    this.bundleItems.forEach((item, id) => {
      if (!item.isMystery) {
        this.removeSelectedState(item.productId);
        this.hideQuantityControls(item.productId);
      }
    });

    this.bundleItems.clear();
    this.productQuantities.clear();

    // Reset mystery flavor
    this.mysteryItemId = null;
    const mysteryCheckbox = document.querySelector('.mystery-checkbox');
    if (mysteryCheckbox) {
      mysteryCheckbox.checked = false;
    }

    // Update UI
    this.updateDisplay();
    this.renderBundleItems();
    this.updateAddToCartButton();
  }

  resetBundleState() {
    // Full reset back to initial state (used after add to cart)
    this.clearBundle();

    // Reset pack size to default (smallest available)
    const availablePacks = this.getAvailablePackSizes();
    const defaultPack = availablePacks[0];
    const boxSizeBtns = document.querySelectorAll('.box-size-btn');
    boxSizeBtns.forEach(btn => btn.classList.remove('selected'));

    const defaultBoxSizeBtn = defaultPack
      ? document.querySelector(`.box-size-btn[data-box-size="${defaultPack.size}"]`)
      : document.querySelector('.box-size-btn');

    if (defaultBoxSizeBtn) {
      defaultBoxSizeBtn.classList.add('selected');
      this.currentPackSize = parseInt(defaultBoxSizeBtn.dataset.boxSize);
      this.currentPackPrice = parseFloat(defaultBoxSizeBtn.dataset.boxPrice) || (defaultPack?.price || 40);
      this.maxItems = this.currentPackSize;
    }

    // Reset subscription and shipping protection
    this.isSubscription = false;
    this.shippingProtection = false;
    this.wasAutoUpsized = false;
    const protectionCheckbox = document.getElementById('shipping-protection-checkbox');
    const mobileProtectionCheckbox = document.getElementById('mobile-shipping-protection-checkbox');
    if (protectionCheckbox) protectionCheckbox.checked = false;
    if (mobileProtectionCheckbox) mobileProtectionCheckbox.checked = false;
    this.toggleSubscription(false);

    // Reset category filter buttons
    document.querySelectorAll('.category-filter-btn').forEach(btn => {
      btn.classList.remove('active');
      const originalColor = btn.dataset.color;
      btn.style.borderColor = originalColor || '#d1d5db';
      btn.style.backgroundColor = 'white';
      btn.style.color = '#374151';
    });

    // Reset manual select button
    const manualBtn = document.querySelector('.manual-select-btn');
    if (manualBtn) {
      manualBtn.classList.add('selected');
    }

    // Show all product cards
    document.querySelectorAll('.bundle-product-card').forEach(card => {
      card.classList.remove('filtered-out');
    });

    // Update price and slot displays
    this.updatePriceDisplay();
    this.updateShippingProtectionPrice();
    this.updateSlotVisibility();
  }

  listenForCartDrawerClose() {
    // Patch BoosterTheme.cart.close to restore scroll (programmatic close doesn't fire change events)
    if (BoosterTheme.cart && !BoosterTheme.cart._closePatched) {
      const originalClose = BoosterTheme.cart.close.bind(BoosterTheme.cart);
      BoosterTheme.cart.close = () => {
        originalClose();
        document.body.style.overflow = '';
      };
      BoosterTheme.cart._closePatched = true;
    }

    // Handle overlay label clicks (these do fire change events on the checkbox)
    const inputs = document.querySelectorAll('[data-minicart-input]');
    const handler = (e) => {
      if (!e.target.checked) {
        document.body.style.overflow = '';
        inputs.forEach(input => input.removeEventListener('change', handler));
      }
    };
    inputs.forEach(input => input.addEventListener('change', handler));
  }

  addToBundle(button) {
    if (this.bundleItems.size >= this.maxItems) {
      // Try to auto-scale to larger bundle size
      if (this.autoScaleToLargerBundle()) {
        // Continue with adding the item after scaling
      } else {
        this.showError('Maximum items reached for largest bundle size');
        return false;
      }
    }

    const productCard = button.closest('.bundle-product-card');
    const productData = JSON.parse(productCard.querySelector('.product-variants-data').textContent);

    // Get selected variant
    const selectedVariant = this.getSelectedVariant(productCard, productData);

    if (!selectedVariant) {
      this.showError('Please select all options before adding to bundle');
      return false;
    }

    // Get collection IDs from product data or data attribute
    const collectionIds = productData.collection_ids ||
      (productCard.dataset.collectionIds ? productCard.dataset.collectionIds.split(',').map(id => parseInt(id)) : []);

    // Create bundle item with Recharge-compatible data
    const bundleItem = {
      id: `${productData.product_id}-${selectedVariant.id}`,
      productId: productData.product_id,
      variantId: selectedVariant.id,
      title: productData.title,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      image: selectedVariant.featured_image || productData.featured_image,
      handle: productData.handle,
      collectionIds: collectionIds // Store collection IDs for Recharge
    };

    // Skip if this exact variant is already in the bundle (no new bag added)
    if (this.bundleItems.has(bundleItem.id)) {
      return false;
    }

    // Add to bundle
    this.bundleItems.set(bundleItem.id, bundleItem);

    // Initialize quantity tracking for this product
    this.productQuantities.set(productData.product_id, 1);

    // Add selected visual state to product card
    this.addSelectedState(productCard, productData.title);

    // Show quantity controls
    this.showQuantityControls(productCard);

    // Hide the Add to Box button
    button.style.display = 'none';

    // Update UI
    this.updateDisplay();
    this.renderBundleItems();
    this.updateAddToCartButton();

    // Fire ATC tracking event
    this.fireAddToCartEvent(bundleItem);

    // Show feedback
    this.showSuccess('Added to bundle!');

    return true;
  }

  removeFromBundle(bundleItemElement) {
    const productId = bundleItemElement.dataset.productId;
    const isMystery = bundleItemElement.dataset.isMystery === 'true';

    // Add removing animation
    bundleItemElement.classList.add('removing');

    // Wait for animation to complete before removing
    setTimeout(() => {
      // Handle mystery item removal
      if (isMystery) {
        this.removeMysteryFlavor();
        // Uncheck the mystery checkbox
        const mysteryCheckbox = document.querySelector('.mystery-checkbox');
        if (mysteryCheckbox) {
          mysteryCheckbox.checked = false;
        }
        return;
      }

      // Find all items with this productId and remove one
      let removedOne = false;
      const keysToCheck = Array.from(this.bundleItems.keys());

      for (const key of keysToCheck) {
        const item = this.bundleItems.get(key);
        if (item && item.productId == productId && !item.isMystery && !removedOne) {
          this.bundleItems.delete(key);
          removedOne = true;
          break;
        }
      }

      // Count remaining items of this product (excluding mystery)
      let remainingCount = 0;
      this.bundleItems.forEach((item) => {
        if (item.productId == productId && !item.isMystery) {
          remainingCount++;
        }
      });

      // Update product quantity tracking
      if (remainingCount === 0) {
        // Remove selected state from product card
        this.removeSelectedState(productId);
        // Remove from quantity tracking
        this.productQuantities.delete(parseInt(productId));
        // Hide quantity controls and show add button again
        this.hideQuantityControls(productId);
      } else {
        // Update quantity tracking
        this.productQuantities.set(parseInt(productId), remainingCount);
        // Update quantity display on product card
        const productCard = document.querySelector(`.bundle-product-card[data-product-id="${productId}"]`);
        if (productCard) {
          const quantityDisplay = productCard.querySelector('.quantity-display');
          if (quantityDisplay) {
            quantityDisplay.textContent = remainingCount;
          }
        }
      }

      this.updateDisplay();
      this.renderBundleItems();
      this.updateAddToCartButton();
    }, 250); // Match animation duration
  }

  showQuantityControls(productCard) {
    const quantityControls = productCard.querySelector('.quantity-controls');
    const quantityDisplay = productCard.querySelector('.quantity-display');
    const productId = productCard.dataset.productId;

    if (quantityControls) {
      quantityControls.style.display = 'flex';
      // Update quantity display
      const quantity = this.productQuantities.get(parseInt(productId)) || 1;
      if (quantityDisplay) {
        quantityDisplay.textContent = quantity;
      }
    }
  }

  hideQuantityControls(productId) {
    const productCard = document.querySelector(`[data-product-id="${productId}"]`);
    if (productCard) {
      const quantityControls = productCard.querySelector('.quantity-controls');
      const addButton = productCard.querySelector('.bundle-add-button-yellow');

      if (quantityControls) {
        quantityControls.style.display = 'none';
      }
      if (addButton) {
        addButton.style.display = 'block';
      }
    }
  }

  // Method to increase quantity by product ID (for modal)
  increaseQuantityById(productId) {
    // Find the product card and call the regular method
    const productCard = document.querySelector(`.bundle-product-card[data-product-id="${productId}"]`);
    if (productCard) {
      const increaseBtn = productCard.querySelector('.quantity-increase');
      if (increaseBtn) {
        this.increaseQuantity(increaseBtn);
        return;
      }
    }
    // Fallback: manually increase if card not found
    this.manualIncreaseQuantity(productId);
  }

  // Method to decrease quantity by product ID (for modal)
  decreaseQuantityById(productId) {
    // Find the product card and call the regular method
    const productCard = document.querySelector(`.bundle-product-card[data-product-id="${productId}"]`);
    if (productCard) {
      const decreaseBtn = productCard.querySelector('.quantity-decrease');
      if (decreaseBtn) {
        this.decreaseQuantity(decreaseBtn);
        return;
      }
    }
    // Fallback: manually decrease if card not found
    this.manualDecreaseQuantity(productId);
  }

  // Manual increase without button element
  manualIncreaseQuantity(productId) {
    const currentQuantity = this.productQuantities.get(productId) || 0;

    if (this.bundleItems.size >= this.maxItems) {
      if (!this.autoScaleToLargerBundle()) {
        this.showError('Maximum items reached for largest bundle size');
        return;
      }
    }

    // Find existing item to duplicate
    const existingItem = Array.from(this.bundleItems.values()).find(item => item.productId === productId);
    if (existingItem) {
      const newItem = {
        ...existingItem,
        id: `${productId}-${existingItem.variantId}-${Date.now()}`
      };
      this.bundleItems.set(newItem.id, newItem);
      this.productQuantities.set(productId, currentQuantity + 1);
      this.fireAddToCartEvent(newItem);
      this.updateDisplay();
      this.renderBundleItems();
      this.updateAddToCartButton();
    }
  }

  // Manual decrease without button element
  manualDecreaseQuantity(productId) {
    const currentQuantity = this.productQuantities.get(productId) || 0;

    if (currentQuantity <= 1) {
      this.removeAllInstancesOfProduct(productId);
      return;
    }

    const productInstances = Array.from(this.bundleItems.values())
      .filter(item => item.productId === productId);

    if (productInstances.length > 0) {
      this.bundleItems.delete(productInstances[0].id);
      this.productQuantities.set(productId, currentQuantity - 1);
      this.updateDisplay();
      this.renderBundleItems();
      this.updateAddToCartButton();
    }
  }

  increaseQuantity(button) {
    const productId = parseInt(button.dataset.productId);
    const currentQuantity = this.productQuantities.get(productId) || 0;

    // Check if we can add more items to the bundle
    if (this.bundleItems.size >= this.maxItems) {
      // Try to auto-scale to larger bundle size
      if (this.autoScaleToLargerBundle()) {
        // Continue with adding the item after scaling
      } else {
        this.showError('Maximum items reached for largest bundle size');
        return false;
      }
    }

    // Get product data to create new bundle item
    const productCard = document.querySelector(`[data-product-id="${productId}"]`);
    const productData = JSON.parse(productCard.querySelector('.product-variants-data').textContent);
    const selectedVariant = this.getSelectedVariant(productCard, productData);

    // Get collection IDs for Recharge
    const collectionIds = productData.collection_ids ||
      (productCard.dataset.collectionIds ? productCard.dataset.collectionIds.split(',').map(id => parseInt(id)) : []);

    // Create a new bundle item for the additional quantity
    const bundleItem = {
      id: `${productId}-${selectedVariant.id}-${Date.now()}`, // Unique ID for each instance
      productId: productId,
      variantId: selectedVariant.id,
      title: productData.title,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      image: selectedVariant.featured_image || productData.featured_image,
      handle: productData.handle,
      collectionIds: collectionIds // Store collection IDs for Recharge
    };

    // Add to bundle
    this.bundleItems.set(bundleItem.id, bundleItem);

    // Update quantity tracking
    this.productQuantities.set(productId, currentQuantity + 1);

    // Update quantity display
    const quantityDisplay = productCard.querySelector('.quantity-display');
    if (quantityDisplay) {
      quantityDisplay.textContent = currentQuantity + 1;
    }

    // Fire ATC tracking event
    this.fireAddToCartEvent(bundleItem);

    // Update UI
    this.updateDisplay();
    this.renderBundleItems();
    this.updateAddToCartButton();

    return true;
  }

  decreaseQuantity(button) {
    const productId = parseInt(button.dataset.productId);
    const currentQuantity = this.productQuantities.get(productId) || 0;

    if (currentQuantity <= 1) {
      // Remove product entirely if quantity would go to 0
      this.removeAllInstancesOfProduct(productId);
      return;
    }

    // Find and remove one instance of this product from bundle
    const productInstances = Array.from(this.bundleItems.values())
      .filter(item => item.productId === productId);

    if (productInstances.length > 0) {
      // Remove the first instance
      this.bundleItems.delete(productInstances[0].id);

      // Update quantity tracking
      this.productQuantities.set(productId, currentQuantity - 1);

      // Update quantity display
      const productCard = document.querySelector(`[data-product-id="${productId}"]`);
      const quantityDisplay = productCard.querySelector('.quantity-display');
      if (quantityDisplay) {
        quantityDisplay.textContent = currentQuantity - 1;
      }

      // Update UI
      this.updateDisplay();
      this.renderBundleItems();
      this.updateAddToCartButton();
    }
  }

  removeAllInstancesOfProduct(productId) {
    // Remove all instances of this product from bundle
    const productInstances = Array.from(this.bundleItems.entries())
      .filter(([id, item]) => item.productId === productId);

    productInstances.forEach(([id]) => {
      this.bundleItems.delete(id);
    });

    // Remove from quantity tracking
    this.productQuantities.delete(productId);

    // Remove selected state and hide quantity controls
    this.removeSelectedState(productId);
    this.hideQuantityControls(productId);

    // Update UI
    this.updateDisplay();
    this.renderBundleItems();
    this.updateAddToCartButton();
  }

  autoScaleToLargerBundle() {
    // Get available pack sizes from settings
    const settings = window.customBundleSettings || {};
    const availableSizes = [...(settings.packSizes || [])].sort((a, b) => a.size - b.size);

    // Find the next larger size
    const nextSize = availableSizes.find(pack => pack.size > this.currentPackSize);

    if (nextSize) {
      // Mark that auto-upsize happened (enables auto-downsize later)
      this.wasAutoUpsized = true;

      // Update bundle settings directly
      this.currentPackSize = nextSize.size;
      this.currentPackPrice = nextSize.price;
      this.maxItems = nextSize.size;

      // Update UI - find and click the box size button
      const nextBoxSizeBtn = document.querySelector(`.box-size-btn[data-box-size="${nextSize.size}"]`);
      if (nextBoxSizeBtn) {
        // Remove selected class from all buttons
        document.querySelectorAll('.box-size-btn').forEach(btn => btn.classList.remove('selected'));
        // Add selected class to new size
        nextBoxSizeBtn.classList.add('selected');
      }

      // Update display and progress
      this.updateDisplay();
      this.updatePriceDisplay();
      this.updateShippingProtectionPrice();
      this.showSuccess(`Automatically upgraded to ${nextSize.size} Pack ShipMint<sup>™</sup> to fit more items!`);
      return true;
    }

    return false; // No larger size available
  }

  // Auto-downsize box when items are removed after an auto-upgrade
  // Only runs if auto-upsize previously happened
  autoDownsizeBox() {
    // Only auto-downsize if we previously auto-upsized
    if (!this.wasAutoUpsized) return;

    const totalQty = this.bundleItems.size;
    const sortedSizes = this.getAvailablePackSizes();
    if (sortedSizes.length === 0) return;

    // Find smallest pack that fits current items
    const smallestFit = sortedSizes.find(pack => pack.size >= totalQty)
      || sortedSizes[sortedSizes.length - 1];

    // Only act if we can shrink (target is smaller than current)
    if (smallestFit.size >= this.currentPackSize) return;

    // Update bundle settings
    this.currentPackSize = smallestFit.size;
    this.maxItems = smallestFit.size;
    this.currentPackPrice = smallestFit.price;

    // Update UI - select the appropriate box size button
    document.querySelectorAll('.box-size-btn').forEach(btn => {
      btn.classList.toggle('selected', parseInt(btn.dataset.boxSize) === smallestFit.size);
    });

    // Update displays
    this.updateSlotVisibility();
    this.updatePriceDisplay();
    this.updateShippingProtectionPrice();
  }

  updateSlotVisibility() {
    const grid = document.querySelector('#bundle-items-grid');
    if (!grid) return;

    // If bundle is empty, keep the empty-state class
    if (this.bundleItems.size === 0) {
      grid.className = `bundle-items-grid pack-${this.currentPackSize} empty-state`;
      return;
    }

    // Update grid layout class (without empty-state since we have items)
    grid.className = `bundle-items-grid pack-${this.currentPackSize}`;

    const slots = grid.querySelectorAll('.bundle-item-slot');
    slots.forEach((slot, index) => {
      if (index < this.maxItems) {
        slot.style.display = 'flex';
      } else {
        slot.style.display = 'none';
      }
    });
  }

  getSelectedVariant(productCard, productData) {
    // Always return the first available variant since we removed variant selection
    return productData.variants.find(variant => variant.available) || productData.variants[0];
  }

  addSelectedState(productCard, productTitle) {
    // Add selected class
    productCard.classList.add('selected');

    // Try to use metafield colors first
    const productData = JSON.parse(productCard.querySelector('.product-variants-data').textContent);
    const metafields = productData.metafields || {};


    if (metafields.primary_color) {
      // Use actual metafield color for border and selection
      const color = metafields.primary_color;
      productCard.setAttribute('style',
        `border-color: ${color} !important; ` +
        `box-shadow: 0 8px 24px ${this.hexToRgba(color, 0.25)} !important; ` +
        `border-width: 0.25rem !important; ` +
        `--selected-color: ${color};`
      );

      // Remove any theme classes since we're using metafield colors
      productCard.classList.remove('theme-pink', 'theme-purple', 'theme-green', 'theme-orange', 'theme-red', 'theme-blue');
    } else {
      // Fallback to theme class based on product name or category
      const themeClass = this.getThemeClass(productTitle);
      productCard.classList.add(themeClass);
    }
  }

  removeSelectedState(productId) {
    const productCard = document.querySelector(`[data-product-id="${productId}"]`);
    if (productCard) {
      productCard.classList.remove('selected');
      // Remove all theme classes
      productCard.classList.remove('theme-pink', 'theme-purple', 'theme-green', 'theme-orange', 'theme-red', 'theme-blue');
      // Clear custom styles completely
      productCard.removeAttribute('style');
    }
  }

  fireAddToCartEvent(item) {
    const priceAmount = item.price * 0.01;

    // GA4 via gtag
    if (typeof gtag === 'function') {
      gtag('event', 'add_to_cart', {
        currency: 'USD',
        value: priceAmount,
        items: [{
          item_id: String(item.variantId),
          item_name: item.title,
          item_brand: 'Buttermints',
          item_variant: item.variantTitle || '',
          price: priceAmount,
          quantity: 1
        }]
      });
    }

    // Meta Pixel (directly loaded)
    if (typeof fbq === 'function') {
      fbq('track', 'AddToCart', {
        content_ids: [String(item.variantId)],
        content_name: item.title,
        content_type: 'product',
        value: priceAmount,
        currency: 'USD'
      });
    }
  }

  getThemeClass(productTitle) {
    const title = productTitle.toLowerCase();

    // Assign theme colors to match specific product background colors
    if (title.includes('blueberry')) {
      return 'theme-purple'; // Purple background like in image
    } else if (title.includes('cinnamint')) {
      return 'theme-red'; // Red/coral background
    } else if (title.includes('cinnamon') && !title.includes('cinnamint')) {
      return 'theme-pink'; // Baby pink background
    } else if (title.includes('peppermint') || title.includes('mint')) {
      return 'theme-green'; // Green background
    } else if (title.includes('strawberry')) {
      return 'theme-red'; // Red background
    } else if (title.includes('raspberry') || title.includes('lemonade')) {
      return 'theme-pink'; // Pink background
    } else if (title.includes('coconut')) {
      return 'theme-orange'; // Beige/tan background
    } else if (title.includes('vanilla') || title.includes('cream')) {
      return 'theme-orange'; // Light orange/cream background
    } else if (title.includes('grape') || title.includes('lavender')) {
      return 'theme-purple'; // Purple background
    } else {
      // Default based on other keywords
      if (title.includes('spice') || title.includes('chili')) {
        return 'theme-red';
      } else if (title.includes('fruit')) {
        return 'theme-pink';
      } else {
        return 'theme-purple'; // Default fallback
      }
    }
  }


  renderBundleItems() {
    // Render both grid and widget views
    this.renderBundleGrid();
    this.renderWidgetList();
  }

  renderBundleGrid() {
    const grid = document.querySelector('#bundle-items-grid');
    if (!grid) return;

    // Check if bundle is empty
    if (this.bundleItems.size === 0) {
      // Show large empty state
      grid.className = `bundle-items-grid pack-${this.currentPackSize} empty-state`;
      grid.innerHTML = '';
      return;
    }

    // Show filled state with grid
    grid.className = `bundle-items-grid pack-${this.currentPackSize}`;

    // Clear and rebuild slots
    grid.innerHTML = '';

    // Group items by product to show quantity (but keep mystery items separate)
    const groupedItems = new Map();
    const mysteryItems = [];

    this.bundleItems.forEach((item) => {
      if (item.isMystery) {
        // Keep mystery items separate
        mysteryItems.push(item);
      } else {
        const key = item.productId;
        if (groupedItems.has(key)) {
          const existing = groupedItems.get(key);
          existing.quantity += 1;
        } else {
          groupedItems.set(key, {
            ...item,
            quantity: 1
          });
        }
      }
    });

    // Create filled slots for grouped items
    groupedItems.forEach((item) => {
      const slot = document.createElement('div');
      slot.className = 'bundle-item-slot filled';
      slot.dataset.itemId = item.id;
      slot.dataset.productId = item.productId;

      const imageUrl = item.image || '';
      // Shorten title for display
      const shortTitle = item.title.length > 20 ? item.title.substring(0, 18) + '...' : item.title;

      slot.innerHTML = `
        <div class="bundle-item-image-wrapper">
          <img src="${imageUrl}" alt="${item.title}" class="bundle-item-image" loading="lazy">
          <button class="bundle-item-remove" type="button" title="Remove ${item.title}">×</button>
        </div>
        <span class="bundle-item-title">${shortTitle}</span>
        <div class="bundle-item-quantity">
          <button type="button" class="bundle-item-qty-btn bundle-qty-decrease${item.quantity === 1 ? ' is-trash' : ''}" data-product-id="${item.productId}">${item.quantity === 1 ? '<svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>' : '−'}</button>
          <span class="bundle-item-qty-value">${item.quantity}</span>
          <button type="button" class="bundle-item-qty-btn bundle-qty-increase" data-product-id="${item.productId}">+</button>
        </div>
      `;

      grid.appendChild(slot);
    });

    // Create slots for mystery items (with hidden image and special styling)
    mysteryItems.forEach((item) => {
      const slot = document.createElement('div');
      slot.className = 'bundle-item-slot filled mystery-slot';
      slot.dataset.itemId = item.id;
      slot.dataset.productId = item.productId;
      slot.dataset.isMystery = 'true';

      slot.innerHTML = `
        <div class="bundle-item-image-wrapper mystery-wrapper">
          <div class="mystery-box-icon">🎁</div>
          <button class="bundle-item-remove mystery-remove" type="button" title="Remove Mystery Flavor">×</button>
        </div>
        <span class="bundle-item-title mystery-title">Mystery Flavor</span>
      `;

      grid.appendChild(slot);
    });

    // Only show empty slots if no items have been added yet (empty state)
    // When items exist, don't show empty placeholder slots
    if (groupedItems.size === 0 && mysteryItems.length === 0) {
      // Show empty state - this is handled by the empty-state class on the grid
    }
    // No empty slots shown when items exist - cleaner look

    // Update max slots based on pack size
    this.updateSlotVisibility();
  }

  renderWidgetList() {
    const widgetList = document.querySelector('#bundle-widget-items-list');
    if (!widgetList) return;

    widgetList.innerHTML = '';

    // Group items by product to show quantity
    const groupedItems = new Map();

    this.bundleItems.forEach((item) => {
      const key = item.productId;
      if (groupedItems.has(key)) {
        const existing = groupedItems.get(key);
        existing.quantity += 1;
      } else {
        groupedItems.set(key, {
          ...item,
          quantity: 1
        });
      }
    });

    // Create list items
    groupedItems.forEach((item) => {
      const listItem = document.createElement('div');
      listItem.className = 'bundle-widget-item';
      listItem.dataset.productId = item.productId;

      const imageUrl = item.image || '';

      listItem.innerHTML = `
        <img src="${imageUrl}" alt="${item.title}" class="bundle-widget-item-image">
        <div class="bundle-widget-item-info">
          <h3 class="bundle-widget-item-title">${item.title}</h3>
        </div>
        <div class="bundle-widget-item-qty">
          <button type="button" class="bundle-widget-qty-btn bundle-qty-decrease${item.quantity === 1 ? ' is-trash' : ''}" data-product-id="${item.productId}">${item.quantity === 1 ? '<svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>' : '−'}</button>
          <span class="bundle-widget-qty-value">${item.quantity}</span>
          <button type="button" class="bundle-widget-qty-btn bundle-qty-increase" data-product-id="${item.productId}">+</button>
        </div>
      `;

      widgetList.appendChild(listItem);
    });
  }

  updateDisplay() {
    // Auto-downsize box if items were removed after an auto-upgrade
    this.autoDownsizeBox();

    const currentCount = this.bundleItems.size;

    // Update cart counter in header
    const cartCountNumber = document.querySelector('.cart-count-number');
    if (cartCountNumber) {
      cartCountNumber.textContent = currentCount;
    }

    // Update count displays
    const countElements = document.querySelectorAll('.count');
    countElements.forEach(el => {
      el.textContent = this.bundleItems.size;
    });

    // Update max count displays
    const maxCountElements = document.querySelectorAll('.max-count');
    maxCountElements.forEach(el => {
      el.textContent = this.maxItems;
    });

    // Update legacy progress bar (if exists)
    const progressBar = document.querySelector('.progress-bar-fill');
    if (progressBar) {
      const percentage = (this.bundleItems.size / this.maxItems) * 100;
      progressBar.style.width = `${percentage}%`;
    }

    // Update milestone progress bar
    this.updateMilestoneProgress();

    // Update price display (considers subscription discount)
    this.updatePriceDisplay();

    // Update mobile sticky bar count
    this.updateMobileStickyBar();
  }

  updateMobileStickyBar() {
    const mobileCount = document.getElementById('mobile-sticky-count');
    if (mobileCount) {
      mobileCount.textContent = this.bundleItems.size;
    }
  }

  openMobileReviewModal() {
    const modal = document.getElementById('mobile-review-modal');
    if (modal) {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      this.updateMobileReviewModal();
    }
  }

  closeMobileReviewModal() {
    const modal = document.getElementById('mobile-review-modal');
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    }
  }

  updateMobileReviewModal() {
    // Update count in header
    const modalCount = document.getElementById('mobile-modal-count');
    const totalQty = this.bundleItems.size;
    if (modalCount) {
      modalCount.textContent = totalQty;
    }

    // Update progress bar based on milestones
    const progressFill = document.getElementById('mobile-progress-fill');
    if (progressFill) {
      const milestones = this.getMilestones();
      const maxMilestone = milestones[milestones.length - 1] || 10;
      let fillPercentage = 0;

      if (totalQty <= 0) {
        fillPercentage = 0;
      } else if (totalQty >= maxMilestone) {
        fillPercentage = 100;
      } else {
        // Find which segment we're in and calculate position
        for (let i = 0; i < milestones.length - 1; i++) {
          const start = milestones[i];
          const end = milestones[i + 1];
          const segmentWidth = 100 / (milestones.length - 1);

          if (totalQty > start && totalQty <= end) {
            const segmentProgress = (totalQty - start) / (end - start);
            fillPercentage = (i * segmentWidth) + (segmentProgress * segmentWidth);
            break;
          } else if (totalQty > end) {
            fillPercentage = (i + 1) * segmentWidth;
          }
        }
      }
      progressFill.style.width = `${fillPercentage}%`;
    }

    // Update markers
    const markers = document.querySelectorAll('.mobile-milestone-marker');
    const milestones = this.getMilestones();
    markers.forEach(marker => {
      const value = parseInt(marker.dataset.milestone);
      marker.classList.remove('reached', 'active');
      if (totalQty >= value) {
        marker.classList.add('reached');
      }
      // Highlight the current milestone - find which range we're in
      const milestoneIndex = milestones.indexOf(value);
      if (milestoneIndex !== -1) {
        const nextMilestone = milestones[milestoneIndex + 1];
        if (value === 0 && totalQty === 0) {
          marker.classList.add('active');
        } else if (nextMilestone !== undefined && totalQty >= value && totalQty < nextMilestone) {
          marker.classList.add('active');
        } else if (nextMilestone === undefined && totalQty >= value) {
          marker.classList.add('active');
        }
      }
    });

    // Update items list - Group by productId
    const itemsContainer = document.getElementById('mobile-review-items');
    if (itemsContainer) {
      if (this.bundleItems.size === 0) {
        itemsContainer.innerHTML = `
          <div class="mobile-review-empty">
            <svg class="mobile-review-empty-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"/>
            </svg>
            <p>No items selected yet</p>
          </div>
        `;
      } else {
        // Group items by productId
        const groupedItems = new Map();
        this.bundleItems.forEach((item) => {
          const productId = item.productId;
          if (groupedItems.has(productId)) {
            groupedItems.get(productId).quantity += 1;
          } else {
            groupedItems.set(productId, {
              productId: productId,
              title: item.title,
              image: item.image,
              quantity: 1
            });
          }
        });

        let itemsHTML = '';
        groupedItems.forEach((item, productId) => {
          itemsHTML += `
            <div class="mobile-review-item" data-product-id="${productId}">
              <img src="${item.image || ''}" alt="${item.title}" class="mobile-review-item-image">
              <div class="mobile-review-item-info">
                <h4 class="mobile-review-item-title">${item.title}</h4>
              </div>
              <div class="mobile-review-item-qty">
                <button type="button" class="mobile-review-qty-btn${item.quantity === 1 ? ' is-trash' : ''}" data-product-id="${productId}" data-action="decrease">${item.quantity === 1 ? '<svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>' : '−'}</button>
                <span class="mobile-review-qty-value">${item.quantity}</span>
                <button type="button" class="mobile-review-qty-btn" data-product-id="${productId}" data-action="increase">+</button>
              </div>
            </div>
          `;
        });
        itemsContainer.innerHTML = itemsHTML;
      }
    }

    // Update checkout button
    const checkoutBtn = document.getElementById('mobile-checkout-btn');
    const checkoutPrice = document.getElementById('mobile-checkout-price');
    const isComplete = totalQty >= this.maxItems;

    if (checkoutBtn) {
      checkoutBtn.disabled = !isComplete;
    }
    if (checkoutPrice) {
      // Show correct price based on current purchase type
      const displayPrice = this.isSubscription
        ? this.formatSubPrice(this.getSubscriptionPrice())
        : this.currentPackPrice;
      checkoutPrice.textContent = `$${displayPrice}`;
    }
  }

  updateMilestoneProgress() {
    const milestones = this.getMilestones();
    const maxMilestone = milestones[milestones.length - 1] || 10;
    const currentCount = this.bundleItems.size;

    // Update milestone markers
    const markers = document.querySelectorAll('.milestone-marker');
    markers.forEach(marker => {
      const milestoneValue = parseInt(marker.dataset.milestone);
      marker.classList.remove('reached', 'active');

      if (currentCount >= milestoneValue) {
        marker.classList.add('reached');
      }
      if (currentCount === milestoneValue) {
        marker.classList.add('active');
      }
    });

    // Update the track fill based on current count position
    const trackFill = document.querySelector('.milestone-fill');
    if (trackFill) {
      // Calculate fill percentage based on position between milestones
      let fillPercentage = 0;

      if (currentCount <= 0) {
        fillPercentage = 0;
      } else if (currentCount >= maxMilestone) {
        fillPercentage = 100;
      } else {
        // Find which segment we're in and calculate position
        for (let i = 0; i < milestones.length - 1; i++) {
          const start = milestones[i];
          const end = milestones[i + 1];
          const segmentWidth = 100 / (milestones.length - 1);

          if (currentCount >= start && currentCount < end) {
            const segmentProgress = (currentCount - start) / (end - start);
            fillPercentage = (i * segmentWidth) + (segmentProgress * segmentWidth);
            break;
          } else if (currentCount >= end) {
            fillPercentage = (i + 1) * segmentWidth;
          }
        }
      }

      trackFill.style.width = `${fillPercentage}%`;
    }
  }

  calculateTotalPrice() {
    // Return fixed pack price based on selected pack size
    // Price is stored in currentPackPrice (in dollars), convert to cents
    return (this.currentPackPrice || 0) * 100;
  }

  updateAddToCartButton() {
    const addButton = document.querySelector('.fsb__bundle-add-to-cart');
    const widgetButton = document.querySelector('.bundle-widget-checkout-btn');

    const currentCount = this.bundleItems.size;
    const canAddToCart = currentCount >= this.maxItems;
    const remainingCount = this.maxItems - currentCount;

    // Update main button (if exists)
    if (addButton) {
      addButton.disabled = !canAddToCart;

      const cartIcon = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="9" cy="21" r="1"></circle>
          <circle cx="20" cy="21" r="1"></circle>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
        </svg>
      `;

      if (canAddToCart) {
        // Show "Save My Bundle" with cart icon when ready
        addButton.innerHTML = `${cartIcon}<span>Save My Bundle</span>`;
      } else {
        // Show remaining count needed
        const flavorText = remainingCount === 1 ? 'flavor' : 'flavors';
        addButton.innerHTML = `<span>Please select ${remainingCount} more ${flavorText}</span>`;
      }
    }

    // Update widget button
    if (widgetButton) {
      widgetButton.disabled = !canAddToCart;

      // Update secondary one-time button disabled state
      const onetimeBtn = document.querySelector('.bundle-widget-onetime-btn');
      if (onetimeBtn) {
        onetimeBtn.disabled = !canAddToCart;
      }

      // Update price on primary checkout button based on current purchase type
      const bundlePriceEls = widgetButton.querySelectorAll('.bundle-price-value');
      if (bundlePriceEls && this.currentPackPrice) {
        const displayPrice = this.isSubscription
          ? this.formatSubPrice(this.getSubscriptionPrice())
          : this.currentPackPrice;

        bundlePriceEls.forEach(el => {
          el.textContent = displayPrice;
        });
      }
    }
  }

  async addBundleToCart() {
    if (this.bundleItems.size !== this.maxItems) {
      this.showError(`Please select ${this.maxItems} items for your bundle`);
      return;
    }

    // Show loading state immediately
    const addButton = document.querySelector('.fsb__bundle-add-to-cart');
    if (addButton) {
      addButton.disabled = true;
      addButton.innerHTML = '<span class="loading-spinner"></span><span>Adding bundle...</span>';
    }

    const settings = this.settings;
    const bundleItemsSnapshot = Array.from(this.bundleItems.values());
    console.log('[BundleBuilder] addToCart started', {
      itemCount: bundleItemsSnapshot.length,
      packSize: this.currentPackSize,
      packPrice: this.currentPackPrice,
      isSubscription: this.isSubscription,
      rechargeEnabled: settings.rechargeEnabled,
      bundleProductId: settings.bundleProductId,
      items: bundleItemsSnapshot.map(i => ({ id: i.productId, variant: i.variantId, title: i.title }))
    });

    try {
      // Check if Recharge is enabled and configured
      if (settings.rechargeEnabled && settings.bundleProductId) {
        console.log('[BundleBuilder] Using Recharge path');
        await this.addRechargeBundleToCart();
      } else {
        console.log('[BundleBuilder] Using legacy path');
        await this.addLegacyBundleToCart();
      }
    } catch (error) {
      console.error('[BundleBuilder] addToCart failed:', error);
      this.showError(error.message || 'Failed to add to cart');
      // Re-enable button on error
      if (addButton) {
        addButton.disabled = false;
        this.updateAddToCartButton();
      }
    }
  }

  async addRechargeBundleToCart() {
    const settings = this.settings;
    const bundleVariantId = this.getBundleVariantForPackSize(this.currentPackSize);

    if (!bundleVariantId) {
      throw new Error('No bundle variant found for pack size ' + this.currentPackSize);
    }

    const bundleItemsArray = Array.from(this.bundleItems.values());

    // Aggregate selections by variant — sum quantities for duplicate variants
    const selectionMap = new Map();
    for (const item of bundleItemsArray) {
      const key = String(item.variantId);
      if (selectionMap.has(key)) {
        selectionMap.get(key).quantity += 1;
      } else {
        selectionMap.set(key, {
          collectionId: String(settings.bundleCollectionId),
          externalProductId: String(item.productId),
          externalVariantId: key,
          quantity: 1
        });
      }
    }
    const selections = Array.from(selectionMap.values());

    console.log('[BundleBuilder] Recharge bundle config:', {
      bundleVariantId,
      bundleProductId: settings.bundleProductId,
      bundleCollectionId: settings.bundleCollectionId,
      packSize: this.currentPackSize,
      isSubscription: this.isSubscription,
      selections
    });

    // Ensure Recharge API is available
    let getBundleIdFn = null;

    if (window.recharge && window.recharge.bundle && typeof window.recharge.bundle.getBundleId === 'function') {
      getBundleIdFn = window.recharge.bundle.getBundleId.bind(window.recharge.bundle);
    } else {
      await this.waitForRechargeBundle(5000);
      if (window.recharge && window.recharge.bundle && typeof window.recharge.bundle.getBundleId === 'function') {
        getBundleIdFn = window.recharge.bundle.getBundleId.bind(window.recharge.bundle);
      }
    }

    if (!getBundleIdFn) {
      console.error('[BundleBuilder] recharge.bundle.getBundleId not available', {
        rechargeExists: !!window.recharge,
        bundleExists: !!(window.recharge && window.recharge.bundle),
        bundleKeys: window.recharge?.bundle ? Object.keys(window.recharge.bundle) : []
      });
      throw new Error('Recharge Bundle API not loaded');
    }

    // Build the bundle object for getBundleId
    const bundle = {
      externalVariantId: String(bundleVariantId),
      externalProductId: String(settings.bundleProductId),
      selections
    };

    console.log('[BundleBuilder] getBundleId input:', JSON.stringify(bundle, null, 2));

    const rbId = await getBundleIdFn(bundle);
    console.log('[BundleBuilder] getBundleId result:', rbId);

    // Build "Contents" line item property — required by Recharge so checkout
    // displays the actual selected items instead of just the bundle quantity.
    // Matches Recharge native format: "1x Product\n2x Product"
    const contentsMap = new Map();
    for (const item of bundleItemsArray) {
      const key = item.title;
      contentsMap.set(key, (contentsMap.get(key) || 0) + 1);
    }
    const contentsValue = Array.from(contentsMap.entries())
      .map(([title, qty]) => `${qty}x ${title}`)
      .join('\n');

    // Build cart item — single parent variant with _rb_id + Contents
    const cartItem = {
      id: bundleVariantId,
      quantity: 1,
      properties: { _rb_id: rbId, Contents: contentsValue }
    };

    // Resolve selling plan from the variant's own allocations
    let sellingPlanId = null;
    if (this.isSubscription) {
      const bundleVariants = settings.bundleVariants || [];
      const variant = bundleVariants.find(v => v.id === bundleVariantId);
      const allocations = variant?.sellingPlanAllocations || [];

      console.log('[BundleBuilder] Variant selling plan allocations:', allocations);

      if (allocations.length > 0) {
        // Try to match by frequency preference
        const frequency = settings.defaultFrequency || '1';
        const frequencyMatch = allocations.find(a => {
          const name = (a.name || '').toLowerCase();
          return name.includes(`${frequency} month`) || name.includes('monthly') || name.includes('every month');
        });
        sellingPlanId = frequencyMatch ? frequencyMatch.sellingPlanId : allocations[0].sellingPlanId;
      }

      if (sellingPlanId) {
        cartItem.selling_plan = parseInt(sellingPlanId);
        console.log('[BundleBuilder] Subscription selling_plan (from variant):', sellingPlanId);
      } else {
        console.warn('[BundleBuilder] No selling plan allocations found for variant:', bundleVariantId);
      }
    } else {
      console.log('[BundleBuilder] One-time purchase, no selling_plan');
    }

    const payload = { items: [cartItem] };
    console.log('[BundleBuilder] Recharge cart payload:', JSON.stringify(payload, null, 2));

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const responseData = await response.json().catch(() => ({}));
    console.log('[BundleBuilder] Recharge cart response:', { status: response.status, ok: response.ok, data: responseData });

    if (!response.ok) {
      // If selling plan error, retry without it
      if (cartItem.selling_plan && responseData.description && responseData.description.toLowerCase().includes('selling plan')) {
        console.warn('[BundleBuilder] Selling plan error, retrying without subscription:', responseData.description);
        delete cartItem.selling_plan;

        const retryPayload = { items: [cartItem] };
        console.log('[BundleBuilder] Retry payload (no selling plan):', JSON.stringify(retryPayload, null, 2));

        const retryResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(retryPayload)
        });

        const retryData = await retryResponse.json().catch(() => ({}));
        console.log('[BundleBuilder] Retry response:', { status: retryResponse.status, ok: retryResponse.ok, data: retryData });

        if (!retryResponse.ok) {
          throw new Error(retryData.description || 'Failed to add to cart');
        }
      } else {
        throw new Error(responseData.description || 'Failed to add to cart');
      }
    }

    // Success
    this.showSuccess('Bundle added to cart!');

    await this.addShippingProtectionToCart();

    const addButton = document.querySelector('.fsb__bundle-add-to-cart');
    if (addButton) {
      addButton.disabled = false;
      this.updateAddToCartButton();
    }

    // Refresh and open theme cart drawer
    if (typeof BoosterTheme !== 'undefined' && BoosterTheme.cart) {
      await BoosterTheme.cart.dispatchListeners({change: true});
      BoosterTheme.cart.open();
      document.body.style.overflow = 'hidden';
      this.listenForCartDrawerClose();
    }

    // Reset bundle to initial state
    this.resetBundleState();
  }

  async addLegacyBundleToCart() {
    // Legacy mode: add individual items to cart
    const items = Array.from(this.bundleItems.values()).map(item => ({
      id: item.variantId,
      quantity: 1,
      properties: {
        '_bundle_item': 'true',
        '_bundle_type': `${this.currentPackSize} Pack Bundle`
      }
    }));

    const legacyPayload = { items };
    console.log('[BundleBuilder] Legacy cart payload:', JSON.stringify(legacyPayload, null, 2));

    // Add to Shopify cart
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(legacyPayload)
    });

    const responseData = await response.json().catch(() => ({}));
    console.log('[BundleBuilder] Legacy cart response:', { status: response.status, ok: response.ok, data: responseData });

    if (!response.ok) {
      throw new Error(responseData.description || 'Failed to add to cart');
    }

    // Success - update UI
    this.showSuccess('Bundle added to cart!');

    // Add shipping protection if opted in
    await this.addShippingProtectionToCart();

    // Reset button state
    const addButton = document.querySelector('.fsb__bundle-add-to-cart');
    if (addButton) {
      addButton.disabled = false;
      this.updateAddToCartButton();
    }

    // Refresh and open theme cart drawer
    if (typeof BoosterTheme !== 'undefined' && BoosterTheme.cart) {
      await BoosterTheme.cart.dispatchListeners({change: true});
      BoosterTheme.cart.open();
      document.body.style.overflow = 'hidden';
      this.listenForCartDrawerClose();
    }

    // Reset bundle to initial state
    this.resetBundleState();
  }

  toggleMysteryFlavor(enabled) {
    if (enabled) {
      // Add a random product from fan favorites
      this.addMysteryFlavor();
    } else {
      // Remove the mystery flavor item
      this.removeMysteryFlavor();
    }
  }

  addMysteryFlavor() {
    // Check if we can add more items
    if (this.bundleItems.size >= this.maxItems) {
      // Try to auto-scale to larger bundle size
      if (!this.autoScaleToLargerBundle()) {
        this.showError('Maximum items reached. Remove an item first to add mystery flavor.');
        // Uncheck the mystery checkbox
        const mysteryCheckbox = document.querySelector('.mystery-checkbox');
        if (mysteryCheckbox) mysteryCheckbox.checked = false;
        return;
      }
    }

    // Get all products from fan favorites category
    const fanFavProducts = document.querySelectorAll('.bundle-product-card[data-categories*="favorites"]');

    if (fanFavProducts.length === 0) {
      this.showError('No fan favorites available for mystery flavor');
      const mysteryCheckbox = document.querySelector('.mystery-checkbox');
      if (mysteryCheckbox) mysteryCheckbox.checked = false;
      return;
    }

    // Filter out products already in the bundle
    const availableProducts = Array.from(fanFavProducts).filter(card => {
      const productId = parseInt(card.dataset.productId);
      return !this.productQuantities.has(productId);
    });

    // If all fan favorites are already in bundle, pick from all fan favorites
    const productsToChooseFrom = availableProducts.length > 0 ? availableProducts : Array.from(fanFavProducts);

    // Pick a random product
    const randomIndex = Math.floor(Math.random() * productsToChooseFrom.length);
    const randomCard = productsToChooseFrom[randomIndex];

    // Get product data
    const productData = JSON.parse(randomCard.querySelector('.product-variants-data').textContent);
    const selectedVariant = this.getSelectedVariant(randomCard, productData);

    // Get collection IDs
    const collectionIds = productData.collection_ids ||
      (randomCard.dataset.collectionIds ? randomCard.dataset.collectionIds.split(',').map(id => parseInt(id)) : []);

    // Create mystery bundle item with special flag
    const mysteryItem = {
      id: `mystery-${productData.product_id}-${selectedVariant.id}`,
      productId: productData.product_id,
      variantId: selectedVariant.id,
      title: '🎁 Mystery Flavor',
      actualTitle: productData.title,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      image: selectedVariant.featured_image || productData.featured_image,
      handle: productData.handle,
      collectionIds: collectionIds,
      isMystery: true
    };

    // Store the mystery item ID for later removal
    this.mysteryItemId = mysteryItem.id;

    // Add to bundle
    this.bundleItems.set(mysteryItem.id, mysteryItem);

    // Update UI
    this.updateDisplay();
    this.renderBundleItems();
    this.updateAddToCartButton();

    // Show feedback (don't reveal which flavor!)
    this.showSuccess('Mystery flavor added! What could it be? 🎁');
  }

  removeMysteryFlavor() {
    // Find and remove the mystery item
    if (this.mysteryItemId && this.bundleItems.has(this.mysteryItemId)) {
      this.bundleItems.delete(this.mysteryItemId);
      this.mysteryItemId = null;

      // Update UI
      this.updateDisplay();
      this.renderBundleItems();
      this.updateAddToCartButton();

      this.showSuccess('Mystery flavor removed');
    }
  }

  toggleSubscription(enabled) {
    if (this.debug) console.log('🔄 toggleSubscription called with:', enabled);
    this.isSubscription = enabled;
    if (this.debug) console.log('✅ this.isSubscription set to:', this.isSubscription);

    // Update New UI to reflect subscription state
    document.querySelectorAll('.purchase-option-btn').forEach(btn => {
      btn.classList.remove('selected');
      if (btn.dataset.type === 'subscribe' && enabled) btn.classList.add('selected');
      if (btn.dataset.type === 'onetime' && !enabled) btn.classList.add('selected');
    });

    // Update UI to reflect subscription state (Legacy)
    const toggleContainer = document.querySelector('.toggle-container');
    if (toggleContainer) {
      toggleContainer.classList.toggle('active', enabled);
    }

    // Update the subscription row visual state (Legacy)
    const subscriptionRow = document.querySelector('.subscription-row');
    if (subscriptionRow) {
      subscriptionRow.classList.toggle('subscription-active', enabled);
    }

    // Hide secondary button and always show "Save My Bundle" as primary
    const subscriptionText = document.querySelector('.subscription-text');
    const onetimeText = document.querySelector('.onetime-text');
    const secondaryBtn = document.querySelector('.bundle-widget-onetime-btn');

    if (subscriptionText && onetimeText) {
      // Always show "Save My Bundle" text
      subscriptionText.style.display = '';
      onetimeText.style.display = 'none';
      if (secondaryBtn) secondaryBtn.style.display = 'none';
    }

    // Update shipping protection visibility (desktop and mobile)
    const protectionWrapper = document.getElementById('shipping-protection-wrapper');
    const protectionIncluded = document.getElementById('shipping-protection-included');
    const mobileProtectionWrapper = document.getElementById('mobile-shipping-protection-wrapper');
    const mobileProtectionIncluded = document.getElementById('mobile-shipping-protection-included');
    if (this.settings.shippingProtectionEnabled) {
      if (protectionWrapper) protectionWrapper.style.display = enabled ? 'none' : '';
      if (protectionIncluded) protectionIncluded.style.display = 'none';
      if (mobileProtectionWrapper) mobileProtectionWrapper.style.display = enabled ? 'none' : '';
      if (mobileProtectionIncluded) mobileProtectionIncluded.style.display = 'none';
      // No shipping protection for subscriptions — clear checkbox too
      if (enabled) {
        this.shippingProtection = false;
        const cb = document.getElementById('shipping-protection-checkbox');
        const mobileCb = document.getElementById('mobile-shipping-protection-checkbox');
        if (cb) cb.checked = false;
        if (mobileCb) mobileCb.checked = false;
      }
    }

    // Hide mobile benefits when one-time is selected
    const mobileBenefits = document.querySelector('.mobile-review-benefits');
    if (mobileBenefits) mobileBenefits.style.display = enabled ? '' : 'none';

    // Hide desktop widget benefits when one-time is selected
    const widgetBenefits = document.querySelector('.widget-benefits-grid');
    if (widgetBenefits) widgetBenefits.style.display = enabled ? '' : 'none';

    // Update the displayed price
    this.updatePriceDisplay();

    // Update add to cart button text to show subscription status
    this.updateAddToCartButton();
  }

  async addShippingProtectionToCart() {
    if (!this.shippingProtection || !this.settings.shippingProtectionEnabled) return;

    const variantId = this.settings.protectionVariants[String(this.currentPackSize)];
    if (!variantId) return;

    try {
      const protectionPayload = { items: [{ id: parseInt(variantId), quantity: 1 }] };
      console.log('[BundleBuilder] Shipping protection payload:', protectionPayload);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(protectionPayload)
      });

      const responseData = await response.json().catch(() => ({}));
      console.log('[BundleBuilder] Shipping protection response:', { status: response.status, ok: response.ok, data: responseData });
    } catch (err) {
      console.error('[BundleBuilder] Failed to add shipping protection:', err);
    }
  }

  updateShippingProtectionPrice() {
    const priceEl = document.getElementById('shipping-protection-price');
    const mobilePriceEl = document.getElementById('mobile-shipping-protection-price');
    if (this.settings.protectionPrices) {
      const price = this.settings.protectionPrices[String(this.currentPackSize)] || 1.43;
      const priceText = `+$${price.toFixed(2)}`;
      if (priceEl) priceEl.textContent = priceText;
      if (mobilePriceEl) mobilePriceEl.textContent = priceText;
    }
  }

  updatePriceDisplay() {
    const totalPrice = this.calculateTotalPrice();

    // Update price elements - no discount applied, just show total
    const priceElements = document.querySelectorAll('.bundle-price');
    priceElements.forEach(el => {
      el.textContent = this.formatMoney(totalPrice);
    });

    // Update Purchase Options Buttons
    if (this.currentPackPrice) {
      const basePrice = this.currentPackPrice;
      const discountPercent = this.getDiscountForPackSize();

      // If a comparePrice is configured for this pack, treat it as the
      // list price: strike-through anchors to it and the subscription
      // discount is computed off it (so the displayed % matches savings
      // vs list, not vs the variant price).
      const packSizes = this.settings.packSizes || [];
      const pack = packSizes.find(p => p.size === this.currentPackSize);
      const comparePrice = pack && pack.comparePrice > basePrice ? pack.comparePrice : 0;
      const strikeAnchor = comparePrice || basePrice;
      const subPrice = this.getSubscriptionPrice();

      // Update One Time Option
      const oneTimePriceEl = document.querySelector('.purchase-option-btn[data-type="onetime"] .final-price');
      if (oneTimePriceEl) oneTimePriceEl.textContent = `$${basePrice}`;

      const oneTimeStrikeEl = document.querySelector('.purchase-option-btn[data-type="onetime"] .strike-price');
      if (oneTimeStrikeEl) {
        if (comparePrice) {
          oneTimeStrikeEl.textContent = `$${comparePrice}`;
          oneTimeStrikeEl.style.display = '';
        } else {
          oneTimeStrikeEl.textContent = '';
          oneTimeStrikeEl.style.display = 'none';
        }
      }

      // Update Subscribe Option
      const subStrikePriceEl = document.querySelector('.purchase-option-btn[data-type="subscribe"] .strike-price');
      if (subStrikePriceEl) subStrikePriceEl.textContent = `$${strikeAnchor}`;

      const subFinalPriceEl = document.querySelector('.purchase-option-btn[data-type="subscribe"] .final-price');
      if (subFinalPriceEl) subFinalPriceEl.textContent = `$${this.formatSubPrice(subPrice)}`;

      // Update save badge with correct discount percentage for this pack size
      const saveBadge = document.querySelector('.save-badge');
      if (saveBadge) saveBadge.textContent = `SAVE ${discountPercent}% EVERY 30 DAYS`;

      // Update secondary one-time button price
      const onetimePriceEl = document.querySelector('.onetime-price-value');
      if (onetimePriceEl) {
        onetimePriceEl.textContent = basePrice;
      }
    }
  }

  getBundleVariantForPackSize(packSize) {
    const settings = this.settings;

    // First, try the pre-detected variant IDs from packSizes array
    const packSizes = settings.packSizes || [];
    const pack = packSizes.find(p => p.size === packSize);
    if (pack?.variantId) {
      return parseInt(pack.variantId);
    }

    // Fallback: search through bundleVariants array
    if (settings.bundleVariants && settings.bundleVariants.length > 0) {
      const packSizeStr = packSize.toString();

      // Try to find exact match first
      for (const variant of settings.bundleVariants) {
        const title = variant.title.toLowerCase();
        if (
          title.includes(`${packSizeStr} pack`) ||
          title.includes(`${packSizeStr}-pack`) ||
          title.includes(`${packSizeStr}pack`)
        ) {
          return variant.id;
        }
      }

      // Try looser match (just contains the number)
      for (const variant of settings.bundleVariants) {
        const title = variant.title;
        // Match standalone number or number at word boundary
        const regex = new RegExp(`\\b${packSizeStr}\\b`);
        if (regex.test(title)) {
          return variant.id;
        }
      }
    }

    // Final fallback: use default bundle variant
    return settings.bundleVariantId;
  }

  switchTab(tabLink) {
    const href = tabLink.getAttribute('href');
    const tabId = href.substring(1); // Remove the # symbol

    // Remove active states from all tabs
    document.querySelectorAll('.tabs__navigation-link').forEach(link => {
      link.classList.remove('active');
    });

    document.querySelectorAll('.tabs__content-item').forEach(content => {
      content.classList.remove('active');
    });

    // Add active state to clicked tab
    tabLink.classList.add('active');

    // Show corresponding content
    const targetContent = document.getElementById(`tab-${tabId}`);
    if (targetContent) {
      targetContent.classList.add('active');
    }
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }

  hexToRgba(hex, alpha) {
    // Remove # if present
    hex = hex.replace('#', '');

    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // Wait for RechargeBundle to be available (ES modules load async)
  waitForRechargeBundle(timeout = 5000) {
    return new Promise((resolve) => {
      // Already available
      if (window.RechargeBundle) {
        if (this.debug) console.log('✅ RechargeBundle already available');
        resolve(true);
        return;
      }

      // Check if it already failed to load
      if (window.RechargeBundleError) {
        if (this.debug) console.log('❌ RechargeBundle previously failed to load:', window.RechargeBundleError);
        resolve(false);
        return;
      }

      let resolved = false;

      // Listen for the RechargeReady event (dispatched when module loads)
      const handleReady = () => {
        if (!resolved) {
          resolved = true;
          clearInterval(checkInterval);
          if (this.debug) console.log('✅ RechargeBundle loaded via RechargeReady event');
          resolve(true);
        }
      };
      window.addEventListener('RechargeReady', handleReady, { once: true });

      // Also poll as fallback (in case event fired before listener attached)
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (resolved) return;

        if (window.RechargeBundle) {
          clearInterval(checkInterval);
          resolved = true;
          window.removeEventListener('RechargeReady', handleReady);
          if (this.debug) console.log('✅ RechargeBundle found via polling');
          resolve(true);
        } else if (window.RechargeBundleError) {
          clearInterval(checkInterval);
          resolved = true;
          window.removeEventListener('RechargeReady', handleReady);
          if (this.debug) console.log('❌ RechargeBundle failed to load:', window.RechargeBundleError);
          resolve(false);
        } else if (Date.now() - startTime > timeout) {
          clearInterval(checkInterval);
          resolved = true;
          window.removeEventListener('RechargeReady', handleReady);
          if (this.debug) {
            console.log('❌ RechargeBundle timeout after', timeout, 'ms');
            console.log('💡 Check Network tab for cdn.rechargeapps.com request');
            console.log('💡 Check if ad blocker is blocking the request');
          }
          resolve(false);
        }
      }, 100);
    });
  }

  showSuccess(message) {
    this.showNotification(message, 'success');
  }

  showError(message) {
    this.showNotification(message, 'error');
  }

  showNotification(message, type = 'info') {
    // Create a modern toast notification
    const toast = document.createElement('div');
    toast.className = `bundle-toast bundle-toast--${type}`;
    toast.innerHTML = message;
    toast.style.cssText = `
      position: fixed;
      top: 2rem;
      right: 2rem;
      background: ${type === 'error' ? '#dc2626' : '#059669'};
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 12px;
      font-weight: 600;
      z-index: 10000;
      box-shadow: none;
      animation: slideInRight 0.3s ease;
    `;

    // Add CSS for animation
    if (!document.querySelector('#bundle-toast-styles')) {
      const styles = document.createElement('style');
      styles.id = 'bundle-toast-styles';
      styles.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(styles);
    }

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'slideOutRight 0.3s ease forwards';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  showNutritionModal(nutritionLink) {
    const productId = nutritionLink.dataset.productId;
    const productCard = nutritionLink.closest('.bundle-product-card');
    const productData = JSON.parse(productCard.querySelector('.product-variants-data').textContent);
    const selectedVariant = this.getSelectedVariant(productCard, productData);

    // Store current product data for modal actions
    this.currentModalProduct = {
      productCard: productCard,
      productData: productData,
      selectedVariant: selectedVariant
    };

    // Populate modal with product data
    this.populateNutritionModal(productData, selectedVariant);

    // Show modal
    const modal = document.getElementById('nutrition-modal');
    if (modal) {
      this._savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this._savedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      modal.style.display = 'flex';
    }
  }

  hideNutritionModal() {
    const modal = document.getElementById('nutrition-modal');
    if (modal) {
      modal.style.display = 'none';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, this._savedScrollY || 0);
    }
    this.currentModalProduct = null;

    // Clear gallery auto-switch interval
    if (this.galleryInterval) {
      clearInterval(this.galleryInterval);
      this.galleryInterval = null;
    }
  }

  setupGalleryDots() {
    const dots = document.querySelectorAll('.gallery-dot');
    const images = document.querySelectorAll('.nutrition-gallery-img');
    const galleryImages = document.querySelector('.nutrition-gallery-images');

    // Clear any existing interval
    if (this.galleryInterval) {
      clearInterval(this.galleryInterval);
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        // Update active states
        this.switchGalleryImage(index);
      });
    });

    // Hover effect - switch to 2nd image on hover
    if (galleryImages) {
      galleryImages.addEventListener('mouseenter', () => {
        this.switchGalleryImage(1);
      });

      galleryImages.addEventListener('mouseleave', () => {
        this.switchGalleryImage(0);
      });
    }

    // Default to first image (product bag) being active
    this.switchGalleryImage(0);

    // Auto-switch images every 5 seconds
    this.galleryInterval = setInterval(() => {
      const currentActive = document.querySelector('.gallery-dot.active');
      const currentIndex = currentActive ? parseInt(currentActive.dataset.slide) : 0;
      const nextIndex = (currentIndex + 1) % dots.length;
      this.switchGalleryImage(nextIndex);
    }, 5000);
  }

  switchGalleryImage(index) {
    const dots = document.querySelectorAll('.gallery-dot');
    const images = document.querySelectorAll('.nutrition-gallery-img');

    dots.forEach(d => d.classList.remove('active'));
    images.forEach(img => img.classList.remove('active'));

    if (dots[index]) {
      dots[index].classList.add('active');
    }
    if (images[index]) {
      images[index].classList.add('active');
    }
  }

  populateNutritionModal(productData, selectedVariant) {
    const metafields = productData.metafields || {};

    // Product images - show bag image and candy close-up image
    const imgElement = document.getElementById('nutrition-product-img');
    const imgElement2 = document.getElementById('nutrition-product-img-2');
    const productCard = this.currentModalProduct?.productCard;

    // First image - product bag
    const bagImage = selectedVariant.featured_image || productData.featured_image || '';

    // Second image - candy close-up (use second product image or hover image)
    let candyImage = '';
    if (productCard) {
      const hoverImg = productCard.querySelector('.bundle-product-card__image--hover');
      if (hoverImg) {
        candyImage = hoverImg.src;
      }
    }
    // Fallback to second image in product images array if available
    if (!candyImage && productData.images && productData.images.length > 1) {
      candyImage = productData.images[1];
    }

    if (imgElement) {
      imgElement.src = bagImage;
      imgElement.alt = productData.title;
    }
    if (imgElement2) {
      imgElement2.src = candyImage || bagImage;
      imgElement2.alt = productData.title + ' - Close up';
    }

    // Set up gallery dot navigation
    this.setupGalleryDots();

    // Product title - use flavor title if available
    const titleElement = document.getElementById('nutrition-product-title');
    if (titleElement) {
      const title = metafields.flavor_title || productData.title;
      titleElement.textContent = title.toUpperCase();
    }

    // Calories - use flavor description if available (usually contains calorie info)
    const caloriesElement = document.getElementById('nutrition-product-calories');
    if (caloriesElement) {
      const flavorDesc = metafields.flavor_description || '';
      caloriesElement.textContent = flavorDesc || '243kcal / 4g sugar';
    }

    // Description - use full description from metafields
    const descElement = document.getElementById('nutrition-product-description');
    if (descElement) {
      // Strip HTML tags if present
      let description = metafields.full_description || productData.description || 'Delicious creamy texture with a hint of magic.';
      description = description.replace(/<[^>]*>/g, '').trim();
      descElement.textContent = description;
    }

    // Rating - Handle Shopify rating object format
    const ratingTextElement = document.getElementById('nutrition-rating-text');
    const starsElement = document.getElementById('nutrition-stars');
    if (ratingTextElement) {
      // Extract numeric rating value - handle object or primitive
      let ratingValue = metafields.rating;
      if (typeof ratingValue === 'object' && ratingValue !== null) {
        // Shopify rating metafield format: { value: 4.9, scale_min: 1, scale_max: 5 }
        ratingValue = ratingValue.value || ratingValue.rating || 4.9;
      }
      ratingValue = parseFloat(ratingValue) || 4.9;

      // Extract rating count
      let ratingCount = metafields.rating_count;
      if (typeof ratingCount === 'object' && ratingCount !== null) {
        ratingCount = ratingCount.value || 24;
      }
      ratingCount = parseInt(ratingCount) || 24;

      ratingTextElement.textContent = `${ratingValue.toFixed(1)} (${ratingCount})`;

      // Update star display
      if (starsElement) {
        const fullStars = Math.floor(ratingValue);
        const hasHalfStar = ratingValue % 1 >= 0.5;
        let starsHTML = '';
        for (let i = 0; i < 5; i++) {
          if (i < fullStars) {
            starsHTML += '<span class="star filled">★</span>';
          } else if (i === fullStars && hasHalfStar) {
            starsHTML += '<span class="star half">★</span>';
          } else {
            starsHTML += '<span class="star empty">☆</span>';
          }
        }
        starsElement.innerHTML = starsHTML;
      }
    }

    // Price
    const priceElement = document.getElementById('nutrition-product-price');
    if (priceElement) {
      priceElement.textContent = this.formatMoney(selectedVariant.price);
    }

    // Populate ingredients text
    const ingredientsTextElement = document.getElementById('nutrition-ingredients-text');
    if (ingredientsTextElement) {
      // Try to extract ingredients from description or use default
      let ingredients = this.extractIngredients(productData.description || '');
      ingredientsTextElement.textContent = ingredients || 'Evaporated Cane Juice, Butter, Agave, Natural Flavors, Citric Acid, All Natural Coloring';
    }

    // Populate allergy warning text
    const allergyTextElement = document.getElementById('nutrition-allergy-text');
    if (allergyTextElement) {
      allergyTextElement.textContent = 'Manufactured in a facility that uses eggs, milk, tree nuts, peanuts, wheat & soybeans';
    }

    // Contains Ingredients Image
    const containsIngredientsContainer = document.getElementById('nutrition-contains-ingredients');
    const containsIngredientsImg = document.getElementById('nutrition-contains-ingredients-img');
    if (metafields.contains_ingredients_image && containsIngredientsContainer && containsIngredientsImg) {
      containsIngredientsImg.src = metafields.contains_ingredients_image;
      containsIngredientsContainer.style.display = 'block';
    } else if (containsIngredientsContainer) {
      containsIngredientsContainer.style.display = 'none';
    }

    // Nutrition Facts Image
    const nutritionImageContainer = document.getElementById('nutrition-facts-image-container');
    const nutritionImage = document.getElementById('nutrition-facts-image');
    if (metafields.nutrition_facts_image && nutritionImageContainer && nutritionImage) {
      nutritionImage.src = metafields.nutrition_facts_image;
      nutritionImageContainer.style.display = 'block';
    } else if (nutritionImageContainer) {
      nutritionImageContainer.style.display = 'none';
    }

    // Update Add to Box button based on availability
    const addBtn = document.getElementById('nutrition-add-to-box');
    if (addBtn) {
      const isAvailable = productData.variants.some(v => v.available);
      if (isAvailable) {
        addBtn.textContent = 'Add to Box';
        addBtn.classList.remove('bundle-notify-btn');
        addBtn.classList.add('nutrition-add-btn');
        addBtn.dataset.outOfStock = '';
      } else {
        addBtn.textContent = 'Notify Me';
        addBtn.classList.add('bundle-notify-btn');
        addBtn.classList.remove('nutrition-add-btn');
        addBtn.dataset.outOfStock = 'true';
      }
    }

    // Extract and display ingredient tags
    this.updateIngredientTags(productData.description || '');

    // Set background using metafield colors
    const imageGallery = document.querySelector('.nutrition-image-gallery');
    let primaryColor = metafields.primary_color;
    let lightColor = metafields.light_color;

    if (imageGallery && primaryColor && lightColor) {
      // Use actual metafield colors for background
      imageGallery.style.background = lightColor;
    } else if (imageGallery) {
      // Fallback to theme-based colors
      const themeClass = this.getThemeClass(productData.title);
      const colors = {
        'theme-pink': { primary: '#e879a9', light: '#fecaca' },
        'theme-purple': { primary: '#a78bfa', light: '#ddd6fe' },
        'theme-green': { primary: '#4ade80', light: '#bbf7d0' },
        'theme-orange': { primary: '#fb923c', light: '#fed7aa' },
        'theme-red': { primary: '#f87171', light: '#fecaca' },
        'theme-blue': { primary: '#60a5fa', light: '#bfdbfe' }
      };
      const theme = colors[themeClass] || colors['theme-pink'];
      imageGallery.style.background = theme.light;
      primaryColor = theme.primary;
      lightColor = theme.light;
    }

    // Update badge colors based on product metafield colors
    this.updateBadgeColors(primaryColor, lightColor);

    // Update "What's Inside" section background color
    const whatsInsideSection = document.getElementById('nutrition-whats-inside');
    if (whatsInsideSection && lightColor) {
      whatsInsideSection.style.background = lightColor;
    }

    // Populate reviews section with Judge.me data
    const reviewRating = parseFloat(metafields.rating) || 0;
    const reviewCount = parseInt(metafields.rating_count) || 0;
    this.populateReviewsSection(reviewRating, reviewCount);
  }

  updateBadgeColors(primaryColor, lightColor) {
    const badges = document.querySelectorAll('.nutrition-badge');

    if (primaryColor && lightColor) {
      // Use the product's light color for badge background
      const badgeBgColor = lightColor;
      const iconColor = this.darkenColor(primaryColor, 0.3);

      badges.forEach(badge => {
        badge.style.backgroundColor = badgeBgColor;
        badge.style.color = iconColor;

        // Also update the SVG stroke color
        const svg = badge.querySelector('.badge-svg');
        if (svg) {
          svg.style.stroke = iconColor;
        }

        // Update label color
        const label = badge.querySelector('.badge-label');
        if (label) {
          label.style.color = iconColor;
        }
      });
    }
  }

  lightenColor(color, amount) {
    // Convert hex to RGB and lighten
    if (!color) return '#f5f0e8';
    const hex = color.replace('#', '');
    const r = Math.min(255, parseInt(hex.substr(0, 2), 16) + Math.round(255 * amount));
    const g = Math.min(255, parseInt(hex.substr(2, 2), 16) + Math.round(255 * amount));
    const b = Math.min(255, parseInt(hex.substr(4, 2), 16) + Math.round(255 * amount));
    return `rgb(${r}, ${g}, ${b})`;
  }

  lightenColorForBg(color) {
    // Create a very light version for button background
    if (!color) return '#f9fafb';
    const hex = color.replace('#', '');
    const r = Math.min(255, Math.round(parseInt(hex.substr(0, 2), 16) * 0.2 + 255 * 0.8));
    const g = Math.min(255, Math.round(parseInt(hex.substr(2, 2), 16) * 0.2 + 255 * 0.8));
    const b = Math.min(255, Math.round(parseInt(hex.substr(4, 2), 16) * 0.2 + 255 * 0.8));
    return `rgb(${r}, ${g}, ${b})`;
  }

  darkenColor(color, amount) {
    // Convert hex to RGB and darken
    if (!color) return '#8B7355';
    const hex = color.replace('#', '');
    const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - Math.round(255 * amount));
    const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - Math.round(255 * amount));
    const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - Math.round(255 * amount));
    return `rgb(${r}, ${g}, ${b})`;
  }

  populateReviewsSection(rating, totalReviews) {
    if (!this.currentModalProduct) return;

    const productHandle = this.currentModalProduct.productData.handle;
    const productUrl = `/products/${productHandle}#judgeme_product_reviews`;

    // Store for pagination
    this.currentReviewsProductHandle = productHandle;
    this.currentReviewsPage = 1;
    this.reviewsPerPage = 5;
    this.allReviews = [];

    // Update button URLs
    const writeReviewBtn = document.getElementById('write-review-btn');
    const viewAllBtn = document.getElementById('view-all-reviews-btn');
    if (writeReviewBtn) writeReviewBtn.href = productUrl;
    if (viewAllBtn) viewAllBtn.href = productUrl;

    // Update summary
    const avgScore = document.getElementById('reviews-avg-score');
    const starsDisplay = document.getElementById('reviews-stars-display');
    const totalCount = document.getElementById('reviews-total-count');

    if (avgScore) avgScore.textContent = rating.toFixed(1);
    if (totalCount) totalCount.textContent = totalReviews;
    if (starsDisplay) {
      const fullStars = Math.floor(rating);
      const hasHalf = rating % 1 >= 0.5;
      let stars = '';
      for (let i = 0; i < 5; i++) {
        stars += i < fullStars || (i === fullStars && hasHalf) ? '★' : '☆';
      }
      starsDisplay.textContent = stars;
    }

    // Generate rating bars
    const ratingBars = document.getElementById('reviews-rating-bars');
    if (ratingBars && totalReviews > 0) {
      const distribution = this.generateRatingDistribution(rating, totalReviews);
      const maxCount = Math.max(...distribution.map(d => d.count));
      ratingBars.innerHTML = distribution.map(d => `
        <div class="rating-bar-row">
          <span class="rating-bar-stars">${'★'.repeat(d.stars)}${'☆'.repeat(5 - d.stars)}</span>
          <div class="rating-bar-track">
            <div class="rating-bar-fill" style="width: ${maxCount > 0 ? (d.count / maxCount) * 100 : 0}%"></div>
          </div>
          <span class="rating-bar-count">${d.count}</span>
        </div>
      `).join('');
    } else if (ratingBars) {
      ratingBars.innerHTML = '';
    }

    // Fetch and display reviews
    this.fetchAllReviews(productHandle);
  }

  async fetchAllReviews(productHandle) {
    const container = document.getElementById('reviews-list-container');
    const pagination = document.getElementById('reviews-pagination');

    if (container) {
      container.innerHTML = '<p style="text-align: center; color: #6b7280; padding: 1rem;">Loading reviews...</p>';
    }

    try {
      // Fetch product page to extract reviews
      const response = await fetch(`/products/${productHandle}`);
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract individual reviews from Judge.me widget
        const reviewElements = doc.querySelectorAll('.jdgm-rev');
        this.allReviews = [];

        reviewElements.forEach(rev => {
          const starsEl = rev.querySelector('.jdgm-rev__stars');
          const authorEl = rev.querySelector('.jdgm-rev__author');
          const bodyEl = rev.querySelector('.jdgm-rev__body');
          const titleEl = rev.querySelector('.jdgm-rev__title');
          const dateEl = rev.querySelector('.jdgm-rev__timestamp');

          // Count filled stars
          let starCount = 5;
          if (starsEl) {
            const filledStars = starsEl.querySelectorAll('.jdgm-star.jdgm--on').length;
            if (filledStars > 0) starCount = filledStars;
          }

          this.allReviews.push({
            stars: starCount,
            author: authorEl ? authorEl.textContent.trim() : 'Anonymous',
            body: bodyEl ? bodyEl.textContent.trim() : '',
            title: titleEl ? titleEl.textContent.trim() : '',
            date: dateEl ? dateEl.textContent.trim() : ''
          });
        });

        if (this.allReviews.length > 0) {
          this.displayReviewsPage(1);
        } else {
          if (container) {
            container.innerHTML = '<p style="text-align: center; color: #6b7280; padding: 1rem;">No reviews yet. Be the first to review!</p>';
          }
          if (pagination) pagination.innerHTML = '';
        }
      }
    } catch (e) {
      if (container) {
        container.innerHTML = '<p style="text-align: center; color: #6b7280; padding: 1rem;">Unable to load reviews.</p>';
      }
    }
  }

  displayReviewsPage(page) {
    this.currentReviewsPage = page;
    const container = document.getElementById('reviews-list-container');
    const pagination = document.getElementById('reviews-pagination');

    if (!container) return;

    const totalPages = Math.ceil(this.allReviews.length / this.reviewsPerPage);
    const start = (page - 1) * this.reviewsPerPage;
    const end = start + this.reviewsPerPage;
    const pageReviews = this.allReviews.slice(start, end);

    // Render reviews
    container.innerHTML = pageReviews.map(review => {
      const initial = review.author.charAt(0).toUpperCase();
      const stars = '★'.repeat(review.stars) + '☆'.repeat(5 - review.stars);

      return `
        <div class="review-card">
          <div class="review-card-header">
            <div class="review-avatar">${initial}</div>
            <div class="review-meta">
              <span class="review-stars">${stars}</span>
              <span class="review-author">${this.escapeHtml(review.author)}</span>
              <span class="review-verified">Verified</span>
            </div>
            ${review.date ? `<span class="review-date">${this.escapeHtml(review.date)}</span>` : ''}
          </div>
          ${review.title ? `<div class="review-title">${this.escapeHtml(review.title)}</div>` : ''}
          <p class="review-body">${this.escapeHtml(review.body)}</p>
        </div>
      `;
    }).join('');

    // Render pagination
    if (pagination && totalPages > 1) {
      let paginationHtml = '';

      // Previous button
      if (page > 1) {
        paginationHtml += `<button class="pagination-btn" data-page="${page - 1}">&lt;</button>`;
      }

      // Page numbers
      for (let i = 1; i <= totalPages; i++) {
        paginationHtml += `<button class="pagination-btn ${i === page ? 'active' : ''}" data-page="${i}">${i}</button>`;
      }

      // Next button
      if (page < totalPages) {
        paginationHtml += `<button class="pagination-btn" data-page="${page + 1}">&gt;</button>`;
      }

      pagination.innerHTML = paginationHtml;

      // Add click handlers
      pagination.querySelectorAll('.pagination-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const targetPage = parseInt(e.target.dataset.page);
          if (targetPage && targetPage !== this.currentReviewsPage) {
            this.displayReviewsPage(targetPage);
          }
        });
      });
    } else if (pagination) {
      pagination.innerHTML = '';
    }
  }

  // Keep the old method for backwards compatibility but it won't be used
  async loadJudgemeWidget(productId, productHandle, productTitle, container) {
    // This method is no longer used - reviews are handled by fetchAllReviews
    const metafields = this.currentModalProduct?.productData?.metafields || {};
    const rating = parseFloat(metafields.rating) || 0;
    const reviewCount = parseInt(metafields.rating_count) || 0;

    if (reviewCount > 0) {
      this.renderSimpleReviewSummary(container, rating, reviewCount, productHandle);
    } else {
      container.innerHTML = `
        <div style="text-align: center; padding: 1.5rem; color: #6b7280;">
          <p style="margin: 0; font-size: 0.875rem;">No reviews yet. Be the first to review!</p>
        </div>
      `;
    }
  }

  renderSimpleReviewSummary(container, rating, reviewCount, productHandle) {
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    let starsHtml = '';
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        starsHtml += '<span style="color: var(--accentColor);">★</span>';
      } else if (i === fullStars && hasHalf) {
        starsHtml += '<span style="color: var(--accentColor);">★</span>';
      } else {
        starsHtml += '<span style="color: #e5e7eb;">★</span>';
      }
    }

    container.innerHTML = `
      <div class="reviews-custom-summary">
        <h3 style="font-size: 1.125rem; font-weight: 700; color: #111827; margin: 0 0 1rem 0; text-align: center;">Customer Reviews</h3>
        <div style="display: flex; align-items: center; justify-content: center; gap: 2rem; flex-wrap: wrap;">
          <div style="text-align: center;">
            <div style="font-size: 2.5rem; font-weight: 900; color: #111827; line-height: 1;">${rating.toFixed(1)}</div>
            <div style="font-size: 1.25rem; letter-spacing: 2px; margin: 0.25rem 0;">${starsHtml}</div>
            <div style="font-size: 0.875rem; color: #6b7280;">Based on ${reviewCount} reviews</div>
          </div>
        </div>
        <div style="text-align: center; margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid #e5e7eb;">
          <a href="/products/${productHandle}#judgeme_product_reviews"
             style="display: inline-block; background: var(--accentColor); color: #111827; padding: 0.75rem 1.5rem; border-radius: 50px; font-weight: 600; text-decoration: none; font-size: 0.875rem;"
             target="_blank">
            Read All Reviews
          </a>
        </div>
      </div>
    `;
  }

  fixReviewsWidget(container, productHandle) {
    // Update the static button URL (button is in HTML, not dynamically added)
    const viewAllBtn = document.getElementById('view-all-reviews-btn');
    if (viewAllBtn) {
      viewAllBtn.href = `/products/${productHandle}#judgeme_product_reviews`;
    }
  }

  renderReviewsFromData(data, container) {
    const reviews = data.reviews || [];
    const rating = data.rating || 0;
    const reviewCount = data.reviewCount || reviews.length;

    let html = `
      <div class="jdgm-custom-widget">
        <div class="jdgm-custom-summary" style="text-align: center; padding: 1rem 0; border-bottom: 1px solid #e5e7eb;">
          <div style="font-size: 2.5rem; font-weight: 700; color: #111827;">${rating.toFixed(2)}</div>
          <div style="color: var(--accentColor); font-size: 1.25rem; letter-spacing: 2px;">★★★★★</div>
          <div style="font-size: 0.875rem; color: #6b7280; margin-top: 0.25rem;">Based on ${reviewCount} reviews</div>
        </div>
        <div class="jdgm-custom-reviews" style="padding-top: 1rem;">
    `;

    reviews.slice(0, 5).forEach(review => {
      const authorName = review.reviewer?.name || review.reviewer_name || 'Verified Buyer';
      const reviewRating = review.rating || 5;
      const title = review.title || '';
      const body = review.body || '';
      const date = review.created_at ? new Date(review.created_at).toLocaleDateString() : '';
      const stars = '★'.repeat(reviewRating) + '☆'.repeat(5 - reviewRating);

      html += `
        <div class="review-item" style="padding: 1rem 0; border-bottom: 1px solid #f3f4f6;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
            <span style="font-weight: 600; color: #111827;">${this.escapeHtml(authorName)}</span>
            <span style="color: var(--accentColor);">${stars}</span>
          </div>
          ${title ? `<div style="font-weight: 600; color: #111827; margin-bottom: 0.25rem;">${this.escapeHtml(title)}</div>` : ''}
          <p style="color: #6b7280; font-size: 0.875rem; line-height: 1.5; margin: 0;">${this.escapeHtml(body)}</p>
          ${date ? `<div style="font-size: 0.75rem; color: #9ca3af; margin-top: 0.5rem;">${date}</div>` : ''}
        </div>
      `;
    });

    html += '</div></div>';
    container.innerHTML = html;
  }

  async fetchJudgemeReviews(productHandle, container, totalReviews) {
    try {
      // Use Shopify shop domain from jdgm global config
      const shopDomain = (window.jdgm && window.jdgm.SHOP_DOMAIN) || 'f54b2f.myshopify.com';
      const publicToken = (window.jdgm && window.jdgm.PUBLIC_TOKEN) || '-Yhc5Qq-6wkYWn9MhqwiIXJBmcM';

      // Method 1: Try Judge.me widget endpoint with shop domain
      let response = await fetch(`https://judge.me/api/v1/widgets/product_review?api_token=${publicToken}&shop_domain=${shopDomain}&handle=${productHandle}&per_page=5`, {
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        const html = await response.text();
        // If we get HTML response, try to parse it or render directly
        if (html && html.includes('jdgm-rev')) {
          container.style.display = 'block';
          container.innerHTML = html;
          return;
        }
      }

      // Method 2: Try reviews_for_widget endpoint
      response = await fetch(`https://judge.me/reviews/reviews_for_widget?url=https://${shopDomain}/products/${productHandle}&shop_domain=${shopDomain}&platform=shopify&per_page=5`);

      if (response.ok) {
        const data = await response.json();
        const reviews = data.reviews || [];

        if (reviews.length > 0) {
          container.style.display = 'block';
          container.innerHTML = reviews.slice(0, 3).map(review => {
            const authorName = review.reviewer?.name || review.reviewer_name || 'Verified Buyer';
            const rating = review.rating || 5;
            const title = review.title || '';
            const body = review.body || review.content || '';
            const date = review.created_at ? new Date(review.created_at).toLocaleDateString() : '';

            return `
              <div class="review-item">
                <div class="review-header">
                  <span class="review-author">${this.escapeHtml(authorName)}</span>
                  <span class="review-stars">${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}</span>
                </div>
                ${title ? `<div class="review-title">${this.escapeHtml(title)}</div>` : ''}
                <p class="review-text">${this.escapeHtml(body)}</p>
                ${date ? `<span class="review-date">${date}</span>` : ''}
              </div>
            `;
          }).join('');
          return;
        }
      }

      // Method 3: Try public badge/reviews API
      response = await fetch(`https://judge.me/api/v1/reviews?shop_domain=${shopDomain}&handle=${productHandle}&per_page=5`);

      if (response.ok) {
        const data = await response.json();
        const reviews = data.reviews || data;

        if (Array.isArray(reviews) && reviews.length > 0) {
          container.style.display = 'block';
          container.innerHTML = reviews.slice(0, 3).map(review => {
            const authorName = review.reviewer?.name || review.author || review.reviewer_name || 'Verified Buyer';
            const rating = review.rating || 5;
            const title = review.title || '';
            const body = review.body || review.content || review.review || '';

            return `
              <div class="review-item">
                <div class="review-header">
                  <span class="review-author">${this.escapeHtml(authorName)}</span>
                  <span class="review-stars">${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}</span>
                </div>
                ${title ? `<div class="review-title">${this.escapeHtml(title)}</div>` : ''}
                <p class="review-text">${this.escapeHtml(body)}</p>
              </div>
            `;
          }).join('');
          return;
        }
      }
    } catch (error) {
      // Silent fail - will show no reviews message below
    }

    // If no real reviews available, show appropriate message
    if (totalReviews > 0) {
      container.style.display = 'block';
      container.innerHTML = `
        <div class="review-item">
          <div class="review-header">
            <span class="review-author">Reviews</span>
          </div>
          <p class="review-text">Reviews are available on the product page. Check back soon for more details!</p>
        </div>
      `;
    } else {
      container.innerHTML = '<p style="text-align: center; color: #6b7280; padding: 1rem;">Be the first to review this product!</p>';
      container.style.display = 'block';
    }
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  generateRatingDistribution(avgRating, totalReviews) {
    // Generate a realistic distribution based on average rating
    // Higher ratings tend to have more 5-star reviews
    const distribution = [
      { stars: 5, weight: 0 },
      { stars: 4, weight: 0 },
      { stars: 3, weight: 0 },
      { stars: 2, weight: 0 },
      { stars: 1, weight: 0 }
    ];

    // Calculate weights based on how close each star level is to the average
    distribution.forEach(d => {
      const diff = Math.abs(d.stars - avgRating);
      // Higher weight for stars closer to average, with bias toward 5-star
      if (d.stars === 5 && avgRating >= 4.5) {
        d.weight = 0.7; // High ratings have lots of 5-stars
      } else if (d.stars === 5 && avgRating >= 4.0) {
        d.weight = 0.5;
      } else if (d.stars === 4 && avgRating >= 4.0) {
        d.weight = 0.3;
      } else if (diff <= 0.5) {
        d.weight = 0.4;
      } else if (diff <= 1) {
        d.weight = 0.2;
      } else if (diff <= 1.5) {
        d.weight = 0.1;
      } else {
        d.weight = 0.05;
      }
    });

    // Normalize weights
    const totalWeight = distribution.reduce((sum, d) => sum + d.weight, 0);
    distribution.forEach(d => {
      d.count = Math.round((d.weight / totalWeight) * totalReviews);
    });

    // Adjust to match exact total
    const currentTotal = distribution.reduce((sum, d) => sum + d.count, 0);
    const diff = totalReviews - currentTotal;
    if (diff !== 0) {
      // Add/remove from the highest weighted category
      distribution[0].count += diff;
    }

    return distribution;
  }

  extractIngredients(description) {
    if (!description) return null;

    // Try to find ingredients section in description
    const lowerDesc = description.toLowerCase();

    // Look for common ingredient patterns
    const patterns = [
      /ingredients?:\s*([^<]+)/i,
      /made with:\s*([^<]+)/i,
      /contains:\s*([^<]+)/i
    ];

    for (const pattern of patterns) {
      const match = description.match(pattern);
      if (match && match[1]) {
        // Clean up the ingredients text
        return match[1].replace(/<[^>]*>/g, '').trim().substring(0, 300);
      }
    }

    // Default ingredients based on product type
    return null;
  }

  updateIngredientTags(description) {
    const ingredientTags = document.getElementById('nutrition-ingredient-tags');
    if (!ingredientTags) return;

    // Extract common ingredients from description
    const commonIngredients = ['sugar', 'butter', 'cream', 'milk', 'vanilla', 'cinnamon', 'coconut', 'strawberry', 'raspberry', 'blueberry', 'lemon', 'caramel', 'chocolate', 'mint', 'spice', 'honey', 'natural flavors', 'agave'];
    const foundIngredients = [];

    if (description) {
      const lowerDesc = description.toLowerCase();
      commonIngredients.forEach(ingredient => {
        if (lowerDesc.includes(ingredient)) {
          foundIngredients.push(ingredient);
        }
      });
    }

    // If we found ingredients, use them; otherwise use defaults
    const ingredients = foundIngredients.length > 0 ? foundIngredients.slice(0, 5) : ['Sugar', 'Butter', 'Natural Flavors'];

    ingredientTags.innerHTML = ingredients
      .map(ingredient => `<span class="ingredient-tag">${ingredient.charAt(0).toUpperCase() + ingredient.slice(1)}</span>`)
      .join('');
  }

  addFromNutritionModal() {
    if (!this.currentModalProduct) return;

    const addBtn = document.getElementById('nutrition-add-to-box');

    // Out of stock — open STOQ notify modal instead
    if (addBtn && addBtn.dataset.outOfStock === 'true') {
      const { productData, selectedVariant } = this.currentModalProduct;
      this.hideNutritionModal();
      if (window._RestockRocket) {
        const stoqProduct = {
          id: productData.product_id,
          variants: productData.variants.map(v => ({ id: v.id, available: v.available }))
        };
        window._RestockRocket.openModal(stoqProduct, String(selectedVariant.id));
      }
      return;
    }

    // Use the existing addToBundle method with the product card's add button
    const addButton = this.currentModalProduct.productCard.querySelector('.bundle-add-button-yellow');
    if (addButton) {
      this.addToBundle(addButton);
      this.hideNutritionModal();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.fsb-wrapper')) {
    new BundleBuilder();
  }
});